import { h, Host, getElement, getRenderingRef, createEvent, proxyCustomElement } from '@stencil/core/internal/client';
export { setAssetPath, setPlatformOptions } from '@stencil/core/internal/client';

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function createCommonjsModule(fn, basedir, module) {
	return module = {
		path: basedir,
		exports: {},
		require: function (path, base) {
			return commonjsRequire();
		}
	}, fn(module, module.exports), module.exports;
}

function commonjsRequire () {
	throw new Error('Dynamic requires are not currently supported by @rollup/plugin-commonjs');
}

createCommonjsModule(function (module, exports) {
(function (global, factory) {
  factory() ;
}(commonjsGlobal, (function () {
  /**
   * Applies the :focus-visible polyfill at the given scope.
   * A scope in this case is either the top-level Document or a Shadow Root.
   *
   * @param {(Document|ShadowRoot)} scope
   * @see https://github.com/WICG/focus-visible
   */
  function applyFocusVisiblePolyfill(scope) {
    var hadKeyboardEvent = true;
    var hadFocusVisibleRecently = false;
    var hadFocusVisibleRecentlyTimeout = null;

    var inputTypesAllowlist = {
      text: true,
      search: true,
      url: true,
      tel: true,
      email: true,
      password: true,
      number: true,
      date: true,
      month: true,
      week: true,
      time: true,
      datetime: true,
      'datetime-local': true
    };

    /**
     * Helper function for legacy browsers and iframes which sometimes focus
     * elements like document, body, and non-interactive SVG.
     * @param {Element} el
     */
    function isValidFocusTarget(el) {
      if (
        el &&
        el !== document &&
        el.nodeName !== 'HTML' &&
        el.nodeName !== 'BODY' &&
        'classList' in el &&
        'contains' in el.classList
      ) {
        return true;
      }
      return false;
    }

    /**
     * Computes whether the given element should automatically trigger the
     * `focus-visible` class being added, i.e. whether it should always match
     * `:focus-visible` when focused.
     * @param {Element} el
     * @return {boolean}
     */
    function focusTriggersKeyboardModality(el) {
      var type = el.type;
      var tagName = el.tagName;

      if (tagName === 'INPUT' && inputTypesAllowlist[type] && !el.readOnly) {
        return true;
      }

      if (tagName === 'TEXTAREA' && !el.readOnly) {
        return true;
      }

      if (el.isContentEditable) {
        return true;
      }

      return false;
    }

    /**
     * Add the `focus-visible` class to the given element if it was not added by
     * the author.
     * @param {Element} el
     */
    function addFocusVisibleClass(el) {
      if (el.classList.contains('focus-visible')) {
        return;
      }
      el.classList.add('focus-visible');
      el.setAttribute('data-focus-visible-added', '');
    }

    /**
     * Remove the `focus-visible` class from the given element if it was not
     * originally added by the author.
     * @param {Element} el
     */
    function removeFocusVisibleClass(el) {
      if (!el.hasAttribute('data-focus-visible-added')) {
        return;
      }
      el.classList.remove('focus-visible');
      el.removeAttribute('data-focus-visible-added');
    }

    /**
     * If the most recent user interaction was via the keyboard;
     * and the key press did not include a meta, alt/option, or control key;
     * then the modality is keyboard. Otherwise, the modality is not keyboard.
     * Apply `focus-visible` to any current active element and keep track
     * of our keyboard modality state with `hadKeyboardEvent`.
     * @param {KeyboardEvent} e
     */
    function onKeyDown(e) {
      if (e.metaKey || e.altKey || e.ctrlKey) {
        return;
      }

      if (isValidFocusTarget(scope.activeElement)) {
        addFocusVisibleClass(scope.activeElement);
      }

      hadKeyboardEvent = true;
    }

    /**
     * If at any point a user clicks with a pointing device, ensure that we change
     * the modality away from keyboard.
     * This avoids the situation where a user presses a key on an already focused
     * element, and then clicks on a different element, focusing it with a
     * pointing device, while we still think we're in keyboard modality.
     * @param {Event} e
     */
    function onPointerDown(e) {
      hadKeyboardEvent = false;
    }

    /**
     * On `focus`, add the `focus-visible` class to the target if:
     * - the target received focus as a result of keyboard navigation, or
     * - the event target is an element that will likely require interaction
     *   via the keyboard (e.g. a text box)
     * @param {Event} e
     */
    function onFocus(e) {
      // Prevent IE from focusing the document or HTML element.
      if (!isValidFocusTarget(e.target)) {
        return;
      }

      if (hadKeyboardEvent || focusTriggersKeyboardModality(e.target)) {
        addFocusVisibleClass(e.target);
      }
    }

    /**
     * On `blur`, remove the `focus-visible` class from the target.
     * @param {Event} e
     */
    function onBlur(e) {
      if (!isValidFocusTarget(e.target)) {
        return;
      }

      if (
        e.target.classList.contains('focus-visible') ||
        e.target.hasAttribute('data-focus-visible-added')
      ) {
        // To detect a tab/window switch, we look for a blur event followed
        // rapidly by a visibility change.
        // If we don't see a visibility change within 100ms, it's probably a
        // regular focus change.
        hadFocusVisibleRecently = true;
        window.clearTimeout(hadFocusVisibleRecentlyTimeout);
        hadFocusVisibleRecentlyTimeout = window.setTimeout(function() {
          hadFocusVisibleRecently = false;
        }, 100);
        removeFocusVisibleClass(e.target);
      }
    }

    /**
     * If the user changes tabs, keep track of whether or not the previously
     * focused element had .focus-visible.
     * @param {Event} e
     */
    function onVisibilityChange(e) {
      if (document.visibilityState === 'hidden') {
        // If the tab becomes active again, the browser will handle calling focus
        // on the element (Safari actually calls it twice).
        // If this tab change caused a blur on an element with focus-visible,
        // re-apply the class when the user switches back to the tab.
        if (hadFocusVisibleRecently) {
          hadKeyboardEvent = true;
        }
        addInitialPointerMoveListeners();
      }
    }

    /**
     * Add a group of listeners to detect usage of any pointing devices.
     * These listeners will be added when the polyfill first loads, and anytime
     * the window is blurred, so that they are active when the window regains
     * focus.
     */
    function addInitialPointerMoveListeners() {
      document.addEventListener('mousemove', onInitialPointerMove);
      document.addEventListener('mousedown', onInitialPointerMove);
      document.addEventListener('mouseup', onInitialPointerMove);
      document.addEventListener('pointermove', onInitialPointerMove);
      document.addEventListener('pointerdown', onInitialPointerMove);
      document.addEventListener('pointerup', onInitialPointerMove);
      document.addEventListener('touchmove', onInitialPointerMove);
      document.addEventListener('touchstart', onInitialPointerMove);
      document.addEventListener('touchend', onInitialPointerMove);
    }

    function removeInitialPointerMoveListeners() {
      document.removeEventListener('mousemove', onInitialPointerMove);
      document.removeEventListener('mousedown', onInitialPointerMove);
      document.removeEventListener('mouseup', onInitialPointerMove);
      document.removeEventListener('pointermove', onInitialPointerMove);
      document.removeEventListener('pointerdown', onInitialPointerMove);
      document.removeEventListener('pointerup', onInitialPointerMove);
      document.removeEventListener('touchmove', onInitialPointerMove);
      document.removeEventListener('touchstart', onInitialPointerMove);
      document.removeEventListener('touchend', onInitialPointerMove);
    }

    /**
     * When the polfyill first loads, assume the user is in keyboard modality.
     * If any event is received from a pointing device (e.g. mouse, pointer,
     * touch), turn off keyboard modality.
     * This accounts for situations where focus enters the page from the URL bar.
     * @param {Event} e
     */
    function onInitialPointerMove(e) {
      // Work around a Safari quirk that fires a mousemove on <html> whenever the
      // window blurs, even if you're tabbing out of the page. ¯\_(ツ)_/¯
      if (e.target.nodeName && e.target.nodeName.toLowerCase() === 'html') {
        return;
      }

      hadKeyboardEvent = false;
      removeInitialPointerMoveListeners();
    }

    // For some kinds of state, we are interested in changes at the global scope
    // only. For example, global pointer input, global key presses and global
    // visibility change should affect the state at every scope:
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('touchstart', onPointerDown, true);
    document.addEventListener('visibilitychange', onVisibilityChange, true);

    addInitialPointerMoveListeners();

    // For focus and blur, we specifically care about state changes in the local
    // scope. This is because focus / blur events that originate from within a
    // shadow root are not re-dispatched from the host element if it was already
    // the active element in its own scope:
    scope.addEventListener('focus', onFocus, true);
    scope.addEventListener('blur', onBlur, true);

    // We detect that a node is a ShadowRoot by ensuring that it is a
    // DocumentFragment and also has a host property. This check covers native
    // implementation and polyfill implementation transparently. If we only cared
    // about the native implementation, we could just check if the scope was
    // an instance of a ShadowRoot.
    if (scope.nodeType === Node.DOCUMENT_FRAGMENT_NODE && scope.host) {
      // Since a ShadowRoot is a special kind of DocumentFragment, it does not
      // have a root element to add a class to. So, we add this attribute to the
      // host element instead:
      scope.host.setAttribute('data-js-focus-visible', '');
    } else if (scope.nodeType === Node.DOCUMENT_NODE) {
      document.documentElement.classList.add('js-focus-visible');
      document.documentElement.setAttribute('data-js-focus-visible', '');
    }
  }

  // It is important to wrap all references to global window and document in
  // these checks to support server-side rendering use cases
  // @see https://github.com/WICG/focus-visible/issues/199
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    // Make the polyfill helper globally available. This can be used as a signal
    // to interested libraries that wish to coordinate with the polyfill for e.g.,
    // applying the polyfill to a shadow root:
    window.applyFocusVisiblePolyfill = applyFocusVisiblePolyfill;

    // Notify interested libraries of the polyfill's presence, in case the
    // polyfill was loaded lazily:
    var event;

    try {
      event = new CustomEvent('focus-visible-polyfill-ready');
    } catch (error) {
      // IE11 does not support using CustomEvent as a constructor directly:
      event = document.createEvent('CustomEvent');
      event.initCustomEvent('focus-visible-polyfill-ready', false, false, {});
    }

    window.dispatchEvent(event);
  }

  if (typeof document !== 'undefined') {
    // Apply the polyfill to the global document, so that no JavaScript
    // coordination is required to use the polyfill in the top-level document:
    applyFocusVisiblePolyfill(document);
  }

})));
});

var Action;
(function (Action) {
  Action["PLAY"] = "play";
  Action["PAUSE"] = "pause";
  Action["STOP"] = "stop";
  Action["ENTER_FULLSCREEN"] = "enterfullscreen";
  Action["EXIT_FULLSCREEN"] = "exitfullscreen";
  Action["MUTE"] = "mute";
  Action["UNMUTE"] = "unmute";
  Action["TEXT_ON"] = "texton";
  Action["TEXT_OFF"] = "textoff";
  Action["GO_TO_LIVE"] = "gotolive";
  Action["NONE"] = "none";
  Action["SEEK"] = "seek";
  Action["SEEK_FORWARD"] = "seekforward";
  Action["SEEK_BACK"] = "seekback";
  Action["VOLUME"] = "volume";
  Action["PREVIEW"] = "preview";
  Action["SETTINGS_ON"] = "settingson";
  Action["SETTINGS_OFF"] = "settingsoff";
  Action["LANGUAGE_SELECT"] = "languageselect";
  Action["QUALITY_SELECT"] = "qualityselect";
  Action["POSTER_PLAY"] = "posterplay";
})(Action || (Action = {}));

var ActiveMode;
(function (ActiveMode) {
  ActiveMode["AUTO"] = "auto";
  ActiveMode["MANUAL"] = "manual";
})(ActiveMode || (ActiveMode = {}));

var ControlFunction;
(function (ControlFunction) {
  ControlFunction["MUTE"] = "mute";
  ControlFunction["PLAY"] = "play";
  ControlFunction["FULLSCREEN"] = "fullscreen";
  ControlFunction["TEXT"] = "text";
  ControlFunction["PLAY_OVERLAY"] = "playOverlay";
  ControlFunction["SETTINGS"] = "settings";
  ControlFunction["VOLUME"] = "volume";
})(ControlFunction || (ControlFunction = {}));

var ControlMode;
(function (ControlMode) {
  ControlMode["NONE"] = "none";
  ControlMode["AD"] = "ad";
  ControlMode["CONTENT"] = "content";
  ControlMode["COMPACT"] = "compact";
})(ControlMode || (ControlMode = {}));

var CssNames;
(function (CssNames) {
  CssNames["HIDDEN"] = "avia-ui-hidden";
  CssNames["ACC_VOL_ON"] = "avia-volume-on";
  CssNames["ACC_VOL_OFF"] = "avia-volume-off";
  CssNames["SEGMENT_MARKER"] = "avia-segment-marker";
  CssNames["INDICATOR_ACTIVE"] = "avia-indicator-active";
  CssNames["ACC_VOL"] = "avia-acc-vol";
  CssNames["ACC_VOL_METER"] = "avia-acc-vol-meter";
  CssNames["ACC_VOL_OVERLAY"] = "avia-vol-meter-overlay";
  CssNames["ACC_FWD"] = "avia-acc-fwd";
  CssNames["ACC_BACK"] = "avia-acc-back";
  CssNames["ACC_ARROW"] = "avia-acc-arrow";
  CssNames["UNMUTE_CTA"] = "avia-unmute-cta";
  CssNames["CONTROLS_VISIBLE"] = "avia-controls-vis";
  CssNames["SPINNER_ACTIVE"] = "avia-spinner";
  CssNames["SPINNER_IDLE"] = "avia-spinner-idle";
  CssNames["SETTINGS_NUDGE_LEFT"] = "avia-settings-nudge-left";
})(CssNames || (CssNames = {}));

var LocalizationId;
(function (LocalizationId) {
  LocalizationId["PLAY"] = "play";
  LocalizationId["PAUSE"] = "pause";
  LocalizationId["STOP"] = "stop";
  LocalizationId["MUTE"] = "mute";
  LocalizationId["UNMUTE"] = "unmute";
  LocalizationId["VOLUME"] = "volume";
  LocalizationId["CLICK_TO_UNMUTE"] = "click-to-unmute";
  LocalizationId["TAP_TO_UNMUTE"] = "tap-to-unmute";
  LocalizationId["LIVE"] = "live";
  LocalizationId["GO_LIVE"] = "go-live";
  LocalizationId["SEEK"] = "seek";
  LocalizationId["SEEK_BACK"] = "seek-back";
  LocalizationId["SEEK_FORWARD"] = "seek-forward";
  LocalizationId["CLOSED_CAPTIONS_OFF"] = "closed-captions-off";
  LocalizationId["CLOSED_CAPTIONS_ON"] = "closed-captions-on";
  LocalizationId["FULL_SCREEN_ENTER"] = "full-screen-enter";
  LocalizationId["FULL_SCREEN_EXIT"] = "full-screen-exit";
  LocalizationId["HOURS"] = "hours";
  LocalizationId["MINUTES"] = "minutes";
  LocalizationId["SECONDS"] = "seconds";
  LocalizationId["VALUE_OF_TOTAL"] = "value-of-total";
  LocalizationId["UNMUTE_CTA"] = "unmute-cta";
  LocalizationId["SETTINGS"] = "settings";
  LocalizationId["LANGUAGE"] = "language";
  LocalizationId["QUALITY"] = "quality";
  LocalizationId["BACK"] = "back";
  LocalizationId["CAPTIONS"] = "captions";
  LocalizationId["AUDIO"] = "audio";
  LocalizationId["CLOSE"] = "close";
  LocalizationId["AUTO"] = "auto";
  LocalizationId["UNKNOWN"] = "unknown";
})(LocalizationId || (LocalizationId = {}));

function debounce(func, delay) {
  let timeout;
  return function () {
    clearTimeout(timeout);
    const args = Array.from(arguments);
    timeout = setTimeout(() => func.apply(null, args), delay);
  };
}
function isEmpty(val) {
  return val === null || val === undefined || val == '';
}
function isUndefined(value) {
  return typeof value === 'undefined';
}
function csvToArray(str) {
  if (isEmpty(str)) {
    return [];
  }
  return str.split(',').map(v => v.trim());
}
function toTimeObject(value = 0) {
  if (value == null || isNaN(value)) {
    value = 0;
  }
  const hms = secToHms(value, 3600).split(":");
  return {
    hours: parseInt(hms[0]),
    minutes: parseInt(hms[1]),
    seconds: parseInt(hms[2])
  };
}
function createLocalizedTimeString(time, localeData) {
  const t = toTimeObject(time);
  const hasHours = t.hours > 0;
  let ret = '';
  if (hasHours) {
    ret += `${t.hours} ${localeData[LocalizationId.HOURS]} `;
  }
  if (t.minutes > 0 || hasHours) {
    ret += `${t.minutes} ${localeData[LocalizationId.MINUTES]} `;
  }
  ret += `${t.seconds} ${localeData[LocalizationId.SECONDS]}`;
  return ret;
}
function getEventPos(event) {
  return {
    // @ts-ignore
    x: (event.pageX - (window.scrollX || window.pageXOffset)) || (event.targetTouches[0].pageX - window.scrollX),
    // @ts-ignore
    y: (event.pageY - (window.scrollY || window.pageYOffset)) || (event.targetTouches[0].pageY - window.scrollY)
  };
}
function valueOfTotal(time, duration, localeData) {
  const value = createLocalizedTimeString(time, localeData);
  const total = createLocalizedTimeString(duration, localeData);
  return localize(localeData[LocalizationId.VALUE_OF_TOTAL], { value, total });
}
function toCssPercent(value) {
  return `${roundTo(value * 100, 2)}%`;
}
// TODO: The following items are all duplicates from avia.util.Util and should be removed.
function secToHms(seconds, max = 61) {
  // default
  if (seconds == null || isNaN(seconds)) {
    return "00:00";
  }
  // logical OR truncation
  seconds = seconds | 0;
  let time = zeroFill(seconds % 60);
  seconds = seconds / 60 | 0;
  time = zeroFill(seconds % 60) + ":" + time;
  seconds = seconds / 60 | 0;
  if (seconds > 0) {
    time = zeroFill(seconds) + ":" + time;
  }
  if (max >= 3600 && time.length === 5) {
    time = "00:" + time;
  }
  return time;
}
function localize(message, context) {
  return template(message, context);
}
function template(input, context, open = "{{", close = "}}") {
  if (!input) {
    return input;
  }
  const regex = new RegExp(`${open}((?:(?!(${open}|${close})).)+)${close}`, "g");
  return input.replace(regex, (match, token) => (!isUndefined(context[token])) ? context[token] : match);
}
function zeroFill(time) {
  return (time < 10) ? "0" + time : time.toString();
}
function mapToRange(value, fromMin, fromMax, toMin = 0, toMax = 1) {
  return (value - fromMin) * (toMax - toMin) / (fromMax - fromMin) + toMin;
}
function roundTo(num, len) {
  return Math.round(num * Math.pow(10, len)) / Math.pow(10, len);
}
function clampValue(value, min, max) {
  return Math.max(min, Math.min(max, value));
}
function blockEvent(event) {
  event.preventDefault();
  event.stopPropagation();
}
function createElementsFromString(html) {
  try {
    const template = document.createElement('template');
    template.innerHTML = html;
    return Array.from(template.content.children);
  }
  catch (error) {
    throw new Error(`Could not create element from string: ${html}`);
  }
}
function applyCssVars(varMap) {
  const { style } = document.documentElement;
  Object.keys(varMap).forEach(key => style.setProperty(key, varMap[key]));
}

const PreviewTimeTransform = {
  DEFAULT: (playerTime) => secToHms(playerTime.contentTime),
  UTC_TIME: (playerTime) => new Date(playerTime.liveStreamUtcTime).toTimeString().split(' ')[0],
};

var UiEvents;
(function (UiEvents) {
  UiEvents["ACTION"] = "uiaction";
})(UiEvents || (UiEvents = {}));

class AviaPluginBase {
  constructor() {
    this.debug = false;
    this.logFmt = 'color: #fcf; font-style: italic;';
    this.player = null;
    this.services = null;
    this.options = null;
    this.name = null;
    this.handlePlayerEvent = this.handlePlayerEvent.bind(this);
  }
  destroy() {
    this.listenToPlayer(false, this.getEventInterests());
    this.player = null;
    this.options = null;
    this.services = null;
    this.avia = null;
  }
  handlePlayerEvent(event) {
  }
  // Return a list of events this plugin needs to listen to;
  // implement in subclass
  getEventInterests() {
    return [];
  }
  sendEvent(data) {
    this.services.event.dispatchPluginEvent(data);
  }
  // Add/remove listeners for the given list of player events
  // Invoke with no arg or with TRUE to add listeners, FALSE to remove
  listenToPlayer(flag, events) {
    const action = flag !== false ? 'on' : 'off';
    events.forEach(event => this.player[action](event, this.handlePlayerEvent));
  }
  log(msg) {
    this.debug && (console === null || console === void 0 ? void 0 : console.log(`%c<Plugin: ${this.name}> ${msg}`, this.logFmt));
  }
  async onRegister(player, options, services) {
    this.player = player;
    this.services = services;
    this.options = options;
    this.name = options.name;
    this.avia = services.avia;
    this.debug = options.debug === true;
    await this.initialize();
    this.listenToPlayer(true, this.getEventInterests());
    this.log(`Registered ${this.name}`);
  }
  async initialize() {
  }
}

const LocalizationData = {
  en: {
    [LocalizationId.PLAY]: 'Play',
    [LocalizationId.PAUSE]: 'Pause',
    [LocalizationId.STOP]: 'Stop',
    [LocalizationId.MUTE]: 'Mute',
    [LocalizationId.UNMUTE]: 'Unmute',
    [LocalizationId.VOLUME]: 'Volume',
    [LocalizationId.CLICK_TO_UNMUTE]: 'Click to unmute',
    [LocalizationId.TAP_TO_UNMUTE]: 'Tap to unmute',
    [LocalizationId.SEEK]: 'Seek',
    [LocalizationId.SEEK_BACK]: 'Seek Backward',
    [LocalizationId.SEEK_FORWARD]: 'Seek Forward',
    [LocalizationId.LIVE]: 'Live',
    [LocalizationId.GO_LIVE]: 'Go Live',
    [LocalizationId.CLOSED_CAPTIONS_OFF]: 'Closed Captions Off',
    [LocalizationId.CLOSED_CAPTIONS_ON]: 'Closed Captions On',
    [LocalizationId.FULL_SCREEN_ENTER]: 'Enter Fullscreen',
    [LocalizationId.FULL_SCREEN_EXIT]: 'Exit Fullscreen',
    [LocalizationId.HOURS]: 'Hours',
    [LocalizationId.MINUTES]: 'Minutes',
    [LocalizationId.SECONDS]: 'Seconds',
    [LocalizationId.VALUE_OF_TOTAL]: '{{value}} of {{total}}',
    [LocalizationId.SETTINGS]: 'Settings',
    [LocalizationId.LANGUAGE]: 'Language',
    [LocalizationId.QUALITY]: 'Quality',
    [LocalizationId.BACK]: 'Back',
    [LocalizationId.CAPTIONS]: 'Captions',
    [LocalizationId.AUDIO]: 'Audio',
    [LocalizationId.CLOSE]: 'Close',
    [LocalizationId.AUTO]: 'Auto',
    [LocalizationId.UNKNOWN]: 'Unknown',
  },
  es: {
    [LocalizationId.PLAY]: 'Reproducir',
    [LocalizationId.PAUSE]: 'Pausa',
    [LocalizationId.STOP]: 'Stop',
    [LocalizationId.MUTE]: 'Silencio',
    [LocalizationId.UNMUTE]: 'Dejar de silenciar',
    [LocalizationId.VOLUME]: 'Volumen',
    [LocalizationId.CLICK_TO_UNMUTE]: 'Haz clic para dejar de silenciar',
    [LocalizationId.TAP_TO_UNMUTE]: 'Toca para dejar de silenciar',
    [LocalizationId.SEEK]: 'Buscar',
    [LocalizationId.SEEK_BACK]: 'Buscar hacia atrás',
    [LocalizationId.SEEK_FORWARD]: 'Buscar adelante',
    [LocalizationId.LIVE]: 'En Vivo',
    [LocalizationId.GO_LIVE]: 'Ir a Vivir',
    [LocalizationId.CLOSED_CAPTIONS_OFF]: 'Subtítulos cerrados',
    [LocalizationId.CLOSED_CAPTIONS_ON]: 'Subtítulos cerrados en',
    [LocalizationId.FULL_SCREEN_ENTER]: 'Ir a pantalla completa',
    [LocalizationId.FULL_SCREEN_EXIT]: 'Salir de pantalla completa',
    [LocalizationId.HOURS]: 'Horas',
    [LocalizationId.MINUTES]: 'Minutos',
    [LocalizationId.SECONDS]: 'Segundos',
    [LocalizationId.VALUE_OF_TOTAL]: '{{value}} de {{total}}',
    [LocalizationId.SETTINGS]: 'Configuraciones',
    [LocalizationId.LANGUAGE]: 'Lengua',
    [LocalizationId.QUALITY]: 'Calidad',
    [LocalizationId.BACK]: 'Volver',
    [LocalizationId.CAPTIONS]: 'Subtítulos',
    [LocalizationId.AUDIO]: 'Audio',
    [LocalizationId.CLOSE]: 'Cerrar',
    [LocalizationId.AUTO]: 'Auto',
    [LocalizationId.UNKNOWN]: 'Desconocida',
  }
};

class AviaUiPlugin extends AviaPluginBase {
  constructor() {
    super();
    this.defaultSeekInc = 10;
    this.accIndicatorDisplayTime = 1500;
    this.playbackStarted = false;
    this.settingsEngaged = false;
    this.firstProgressReceived = false;
    this.defaultCompactViewBreakpoint = 520;
    this.rootElement = 'avia-ui';
    this.previewTimeTransform = PreviewTimeTransform.DEFAULT;
    this.aviaUi = null;
    this.hUiAction = this.hUiAction.bind(this);
    this.accVolUpdater = debounce((state) => {
      this.setAccIndicatorState(state);
    }, this.accIndicatorDisplayTime);
    this.accSeekUpdater = debounce((state) => {
      this.setAccIndicatorState(state);
    }, this.accIndicatorDisplayTime);
  }
  static async create(services, options) {
    const plugin = new AviaUiPlugin();
    await plugin.onRegister(services.player, options, services);
    return plugin;
  }
  get activeMode() {
    return this.aviaUi.activeMode || ActiveMode.AUTO;
  }
  set activeMode(value) {
    this.aviaUi.activeMode = value;
  }
  get active() {
    return this.aviaUi.active;
  }
  set active(value) {
    this.setActivityState(value);
  }
  destroy() {
    var _a;
    super.destroy();
    (_a = this.aviaUi) === null || _a === void 0 ? void 0 : _a.removeEventListener(UiEvents.ACTION, this.hUiAction);
    this.aviaUi = null;
  }
  // events from UI
  hUiAction(e) {
    const action = e.detail.action;
    this.services.event.dispatchPluginEvent({
      name: this.name,
      action,
      value: e.detail.value || null
    });
    switch (action) {
      case Action.POSTER_PLAY:
      case Action.PLAY:
      case Action.PAUSE:
      case Action.STOP:
        if (!this.playbackStarted) {
          this.cancelPoster(action === Action.POSTER_PLAY);
          action !== Action.STOP && this.player.play().catch(() => { });
        }
        else {
          // @ts-ignore
          this.player[action]();
        }
        break;
      case Action.ENTER_FULLSCREEN:
        this.player.enterFullscreen();
        break;
      case Action.EXIT_FULLSCREEN:
        this.player.exitFullscreen();
        break;
      case Action.MUTE:
        this.player.muted = true;
        this.aviaUi.muted = true;
        break;
      case Action.UNMUTE:
        this.unmute();
        break;
      case Action.TEXT_ON:
      case Action.TEXT_OFF:
        const isOn = action === Action.TEXT_ON;
        this.player.textTrackEnabled = isOn;
        this.aviaUi.textTrackEnabled = isOn;
        break;
      case Action.SEEK:
        this.playerSeek(e.detail.value);
        break;
      case Action.SEEK_FORWARD:
        this.incrementalSeek(1);
        break;
      case Action.SEEK_BACK:
        this.incrementalSeek(-1);
        break;
      case Action.VOLUME:
        this.player.volume = e.detail.value;
        break;
      case Action.PREVIEW:
        const { time, percent } = e.detail.value;
        const { contentDuration, streamDuration, liveStreamUtcDuration, liveStreamUtcStart } = this.player;
        const streamTime = percent * streamDuration;
        Promise
          .resolve(this.previewTimeTransform({
          contentTime: time,
          streamTime,
          liveStreamUtcTime: Math.round((streamTime * 1000) + liveStreamUtcStart),
          contentDuration: contentDuration,
          streamDuration: streamDuration,
          liveStreamUtcDuration: liveStreamUtcDuration,
        }))
          .then(value => {
          var _a, _b;
          this.aviaUi.preview = {
            value,
            data: (_b = (_a = this.player) === null || _a === void 0 ? void 0 : _a.getThumbnail(time)) === null || _b === void 0 ? void 0 : _b.data,
          };
        });
        break;
      case Action.SETTINGS_ON:
      case Action.SETTINGS_OFF:
        this.setStateForSettings(action === Action.SETTINGS_ON);
        break;
      case Action.LANGUAGE_SELECT:
        this.selectLanguage(e.detail.value);
        break;
      case Action.QUALITY_SELECT:
        this.selectQuality(e.detail.value);
        break;
      case Action.GO_TO_LIVE:
        this.player.goLive();
        break;
    }
  }
  handlePlayerEvent(event) {
    const { ActiveState, PlayerEvent, StreamType } = this.avia;
    const isLive = this.player.streamType === StreamType.LIVE;
    const isDvr = this.player.streamType === StreamType.DVR;
    const { type, data } = event;
    switch (type) {
      case PlayerEvent.READY:
        this.setPlayState();
        this.setPosterState();
        break;
      case PlayerEvent.AUTOPLAY_BLOCKED:
        this.setPosterState(true);
        break;
      case PlayerEvent.RESOURCE_START:
        this.setPosterState(this.aviaUi.posterActive);
        this.setSegmentBreakPositions([]);
        break;
      case PlayerEvent.AD_CUEPOINTS_AVAILABLE:
        this.setSegmentBreakPositions(data.cuepoints);
        break;
      case PlayerEvent.AD_SEGMENT_START:
        if (this.options.showAdControlsOnAdSegmentStart !== false) {
          this.setStateForAdBreakStart();
        }
        break;
      case PlayerEvent.AD_START:
        this.setControlMode(ControlMode.AD);
        this.enableControls([ControlFunction.PLAY], true);
        this.aviaUi.playing = true;
        break;
      case PlayerEvent.AD_BREAK_START:
        this.setStateForAdBreakStart();
        break;
      case PlayerEvent.AD_PROGRESS:
        this.setAdPosition(data.currentTime, data.duration);
        break;
      case PlayerEvent.AD_SEGMENT_END:
      case PlayerEvent.AD_BREAK_COMPLETE:
        this.setControlMode(ControlMode.NONE);
        break;
      case PlayerEvent.CONTENT_START:
      case PlayerEvent.CONTENT_SEGMENT_START:
        this.setStateForContentStart(isLive, isDvr);
        break;
      case PlayerEvent.CONTENT_SEGMENT_END:
        this.firstProgressReceived = false;
        this.setControlMode(ControlMode.NONE);
        break;
      case PlayerEvent.CONTENT_PLAYING:
        this.setControlMode(ControlMode.CONTENT);
        this.setPlayState();
        break;
      case PlayerEvent.AD_PAUSED:
      case PlayerEvent.CONTENT_PAUSED:
        this.aviaUi.playing = false;
        break;
      case PlayerEvent.AD_PLAYING:
      case PlayerEvent.CONTENT_PLAYING:
        this.aviaUi.playing = true;
        break;
      case PlayerEvent.CONTENT_PROGRESS:
        if (!this.firstProgressReceived) {
          this.firstProgressReceived = true;
          this.setPlayState();
          this.setLiveState(isLive, isDvr);
        }
        if (!isLive) {
          this.setTimeAndPosition();
        }
        if (isLive || isDvr) {
          this.setLiveState(isLive, isDvr);
        }
        break;
      case PlayerEvent.FULLSCREEN_ENTER:
      case PlayerEvent.FULLSCREEN_EXIT:
        this.aviaUi.fullscreen = type === PlayerEvent.FULLSCREEN_ENTER;
        break;
      case PlayerEvent.TEXT_TRACK_AVAILABLE:
        this.enableControls(ControlFunction.TEXT, true);
        this.enableSettingsButton();
        break;
      case PlayerEvent.TEXT_TRACK_DISPLAY_MODE_CHANGE:
        this.aviaUi.textTrackEnabled = data.enabled;
        break;
      case PlayerEvent.LANGUAGE_CHANGE:
        this.setLocalizationData(data.value || 'en');
        break;
      case PlayerEvent.MUTE_CHANGE:
        this.aviaUi.muted = data;
        if (!this.player.muted) {
          this.aviaUi.unmuteCtaVisible = false;
        }
        break;
      case PlayerEvent.VOLUME_CHANGE:
        const vol = data;
        const muted = this.player.muted;
        if (this.aviaUi.volume == vol) {
          break;
        }
        if (!this.aviaUi.muted) {
          this.aviaUi.muted = Math.ceil(vol) ? false : true;
        }
        else if (this.aviaUi.muted && vol > 0) {
          this.unmute();
        }
        this.aviaUi.volume = vol;
        if (!muted) {
          this.aviaUi.unmuteCtaVisible = false;
        }
        break;
      case PlayerEvent.HOTKEY_ACTION:
        this.playbackStarted && this.setAccIndicator(data.action, data);
        break;
      case PlayerEvent.ACTIVE_CHANGE:
        if (this.activeMode === ActiveMode.AUTO) {
          this.active = data.value === ActiveState.ACTIVE;
        }
        break;
      case PlayerEvent.PRESENTATION_SIZE_CHANGE:
        this.respondToSizeChange();
        break;
      case PlayerEvent.QUALITY_CHANGE:
        this.setStateForSettings();
        break;
      case PlayerEvent.QUALITY_INFO_CHANGE:
      case PlayerEvent.AUDIO_TRACK_INFO_CHANGE:
        this.enableSettingsButton();
        this.setStateForSettings();
        break;
      case PlayerEvent.RESOURCE_END:
      case PlayerEvent.RESOURCE_INTERRUPTED:
        this.setIdleState();
        break;
      case PlayerEvent.PLAYBACK_STATE_CHANGE:
        this.setPlayState();
        break;
    }
  }
  ////////////
  incrementalSeek(dir) {
    const inc = this.options.seekIncrement || this.defaultSeekInc;
    const increment = dir * inc;
    this.playerSeek(this.player.contentTime + increment);
    this.player.recordMetric(this.avia.MetricType.UI, this.avia.UiMetric.SEEK_BY, increment);
  }
  playerSeek(pos) {
    this.player.seek(pos).then(() => { });
  }
  unmute() {
    this.player.muted = false;
    this.aviaUi.muted = false;
  }
  setControlMode(mode) {
    if (mode === ControlMode.CONTENT) {
      const m = this.getContentControlMode();
      this.aviaUi.controlMode = m;
    }
    else {
      this.aviaUi.controlMode = mode;
    }
  }
  setActivityState(active) {
    var _a, _b;
    if (!this.playbackStarted) {
      return;
    }
    if (this.options.offsetTimedTextOnControlsVisible) {
      const c = this.getContainer();
      c.classList.toggle(CssNames.CONTROLS_VISIBLE, active);
    }
    this.aviaUi.active = this.activeMode === ActiveMode.MANUAL ? active : ((_b = (_a = this.aviaUi) === null || _a === void 0 ? void 0 : _a.settings) === null || _b === void 0 ? void 0 : _b.active) ? true : active;
  }
  setAccIndicator(action, data) {
    const newState = {};
    const { AccessibilityAction } = this.avia;
    switch (action) {
      case AccessibilityAction.INCREASE_VOLUME:
      case AccessibilityAction.DECREASE_VOLUME:
      case AccessibilityAction.TOGGLE_MUTE:
        newState.volumeKey = true;
        this.accVolUpdater({ volumeKey: false });
        if (!this.player.muted)
          this.aviaUi.unmuteCtaVisible = false;
        break;
      case AccessibilityAction.SEEK_FORWARD:
      case AccessibilityAction.SEEK_BACK:
        newState.seekForward = action === AccessibilityAction.SEEK_FORWARD;
        newState.seekBack = action === AccessibilityAction.SEEK_BACK;
        newState.seekTime = (data === null || data === void 0 ? void 0 : data.position) || 0;
        this.accSeekUpdater({ seekForward: false, seekBack: false, seekTime: null });
        break;
    }
    this.setAccIndicatorState(newState);
  }
  setAccIndicatorState(state) {
    this.aviaUi.accIndicatorState = Object.assign(Object.assign({}, this.aviaUi.accIndicatorState), state);
  }
  setStateForContentStart(isLive, isDvr) {
    this.cancelPoster(false);
    this.setControlMode(ControlMode.CONTENT);
    this.setLiveState(isLive, isDvr);
    this.enableControls([ControlFunction.MUTE, ControlFunction.PLAY, ControlFunction.FULLSCREEN], true);
    this.aviaUi.muted = this.player.muted;
    this.setSeekDisabled(false);
    this.setStateForPlaybackStart();
    this.aviaUi.volume = this.player.volume;
    this.enableSettingsButton();
  }
  setStateForAdBreakStart() {
    const enabled = [ControlFunction.MUTE, ControlFunction.PLAY];
    if (this.services.system.isDesktop) {
      enabled.push(ControlFunction.FULLSCREEN);
    }
    else {
      this.enableControls([ControlFunction.FULLSCREEN], false);
    }
    this.cancelPoster(false);
    this.setControlMode(ControlMode.AD);
    this.enableControls(enabled, true);
    this.aviaUi.playing = true;
    this.aviaUi.muted = this.player.muted;
    this.setStateForPlaybackStart();
  }
  setStateForPlaybackStart() {
    if (this.options.useUnmuteCta && !this.playbackStarted && this.player.muted) {
      this.aviaUi.unmuteCtaVisible = true;
    }
    this.playbackStarted = true;
    this.setPlayState();
  }
  setPosterState(active = this.services.playerOptions.autoplay === this.avia.Autoplay.NONE) {
    var _a;
    this.aviaUi.posterImageUrl = ((_a = this.player.resource) === null || _a === void 0 ? void 0 : _a.location.posterImageUrl) || this.options.posterImageUrl;
    this.aviaUi.posterActive = active;
  }
  cancelPoster(posterClicked) {
    this.aviaUi.posterActive = false;
    if (posterClicked) {
      this.services.event.dispatchPluginEvent({
        pluginName: this.name,
        type: Action.POSTER_PLAY
      });
    }
  }
  setIdleState() {
    this.setControlMode(ControlMode.NONE);
    this.aviaUi.unmuteCtaVisible = false;
    this.enableControls([
      ControlFunction.MUTE,
      ControlFunction.PLAY,
      ControlFunction.FULLSCREEN,
      ControlFunction.TEXT
    ], false);
    this.aviaUi.active = false;
    this.playbackStarted = false;
  }
  setLiveState(isLive, isDvr) {
    const isPlayingLive = this.player.isPlayingLive;
    this.aviaUi.isLiveLinear = isLive;
    this.aviaUi.isDvr = isDvr;
    this.aviaUi.live = isLive || (isDvr && isPlayingLive);
    this.aviaUi.pausable = this.player.pausable;
    if (isDvr) {
      this.aviaUi.seekForwardDisabled = isPlayingLive;
    }
  }
  setPlayState() {
    const { playbackState } = this.player;
    const isPlaying = playbackState === this.avia.PlaybackState.PLAYING;
    const isWaiting = playbackState === this.avia.PlaybackState.WAITING;
    this.aviaUi.playing = isWaiting || isPlaying ? true : false;
    this.aviaUi.playDisabled = !isPlaying && playbackState !== this.avia.PlaybackState.PAUSED;
    this.aviaUi.waiting = isWaiting;
  }
  respondToSizeChange() {
    const m = this.aviaUi.controlMode;
    if (m == ControlMode.CONTENT || m == ControlMode.COMPACT) {
      this.aviaUi.controlMode = this.getContentControlMode();
    }
  }
  getContentControlMode() {
    const { width } = this.player.dimensions;
    const brk = this.options.compactViewBreakpoint || this.defaultCompactViewBreakpoint;
    return width > brk ? ControlMode.CONTENT : ControlMode.COMPACT;
  }
  enableSettingsButton() {
    var _a, _b, _c, _d;
    const p = this.player;
    const t = (_a = p.textTrackInfo) === null || _a === void 0 ? void 0 : _a.track;
    const a = (_b = p.audioTrackInfo) === null || _b === void 0 ? void 0 : _b.track;
    const q = ((_d = (_c = p.qualityInfo) === null || _c === void 0 ? void 0 : _c.qualities) === null || _d === void 0 ? void 0 : _d.length) > 1;
    this.aviaUi.settingsDisabled = !(t || a || q);
  }
  setStateForSettings(active = this.settingsEngaged) {
    const p = this.player;
    if (this.services.system.browser === this.avia.Browser.FIREFOX && active) {
      this.aviaUi.classList.add(CssNames.SETTINGS_NUDGE_LEFT);
    }
    this.aviaUi.settings = {
      active,
      textTrackInfo: p.textTrackInfo,
      audioTrackInfo: p.audioTrackInfo,
      qualityInfo: p.qualityInfo,
    };
    this.settingsEngaged = active;
    this.pauseOnSettings(active);
  }
  pauseOnSettings(active) {
    if (this.options.pauseOnSettings) {
      const playing = this.player.playbackState === this.avia.PlaybackState.PLAYING;
      if (active && playing) {
        this.player.pause();
        this.playPendingAfterSettings = true;
      }
      if (!active && this.playPendingAfterSettings) {
        this.playPendingAfterSettings = false;
        this.player.play();
      }
    }
  }
  enableControls(name, flag) {
    const arr = !Array.isArray(name) ? [name] : name;
    const uir = this.aviaUi;
    arr.forEach((n) => uir[`${n}Disabled`] = !flag);
  }
  setSeekDisabled(flag) {
    this.aviaUi.seekDisabled = flag;
    this.aviaUi.seekBackDisabled = flag;
    this.aviaUi.seekForwardDisabled = flag;
  }
  setAdPosition(time, dur) {
    this.aviaUi.adPlayheadPosition = time / dur;
  }
  setTimeAndPosition() {
    if (this.aviaUi.waiting) {
      return;
    }
    const ct = this.player.contentTime;
    const dur = this.player.contentDuration;
    this.aviaUi.timeDisplayValue = this.getTimeDisplayValue(ct, dur);
    this.aviaUi.currentTimeSeconds = ct;
    this.aviaUi.durationSeconds = dur;
    this.aviaUi.playheadPosition = ct / dur;
  }
  setSegmentBreakPositions(times) {
    const d = this.player.contentDuration;
    this.aviaUi.segmentBreakPositions = times.filter(t => t > 0 && t != d).join(',');
  }
  selectLanguage(data) {
    var _a, _b;
    const t = data.trackType;
    const id = data.trackId;
    if (t == 'text') {
      const track = (_a = this.player.textTrackInfo.tracks) === null || _a === void 0 ? void 0 : _a.find((tk) => tk.id == id);
      if (track)
        this.player.textTrack = track;
    }
    else if (t == 'audio') {
      const track = (_b = this.player.audioTrackInfo.tracks) === null || _b === void 0 ? void 0 : _b.find((tk) => tk.index == id);
      if (track)
        this.player.audioTrack = track;
    }
  }
  selectQuality(data) {
    var _a, _b;
    const { index } = data;
    if (index == -1) {
      this.player.autoQualitySwitching = true;
      return;
    }
    const bitrate = (_b = (_a = this.player.qualityInfo.qualities) === null || _a === void 0 ? void 0 : _a[index]) === null || _b === void 0 ? void 0 : _b.bitrate;
    if (bitrate) {
      this.player.bitrate = bitrate;
    }
  }
  getTimeDisplayValue(ct, dur) {
    if (dur > 86400)
      return '';
    return `${secToHms(ct)} / ${secToHms(dur)}`;
  }
  ////////////////////////
  // initialization & util
  setControlExclusions() {
    var _a, _b;
    const info = (_b = (_a = this.services) === null || _a === void 0 ? void 0 : _a.system) === null || _b === void 0 ? void 0 : _b.info;
    const { Device } = this.avia;
    let exc = [];
    switch (info.device) {
      case Device.ANDROID_PHONE:
      case Device.IPHONE:
      case Device.IPOD:
      case Device.KINDLE:
      case Device.IPAD:
      case Device.MOBILE:
      case Device.TABLET:
      case Device.UNKNOWN:
      case Device.WINDOWS_PHONE:
        exc = [ControlFunction.VOLUME, ControlFunction.TEXT, ControlFunction.SETTINGS];
        break;
      case Device.LG_SMART_TV:
      case Device.PLAYSTATION_4:
      case Device.PLAYSTATION_5:
      case Device.SAMSUNG_SMART_TV:
      case Device.VIZIO_SMART_TV:
      case Device.XBOX_ONE:
        exc = [ControlFunction.VOLUME];
        break;
    }
    this.aviaUi.excludedControls = exc;
  }
  createUi() {
    var _a;
    this.applyTheme((_a = this.options) === null || _a === void 0 ? void 0 : _a.theme);
    return customElements.whenDefined(this.rootElement).then(() => {
      this.setControlExclusions();
      this.initializeState();
    });
  }
  getContainer() {
    var _a;
    let container = this.options.container;
    if (container) {
      container = document.querySelector(container);
    }
    return container || ((_a = this.services) === null || _a === void 0 ? void 0 : _a.dom.main);
  }
  initializeState() {
    var _a, _b, _c, _d;
    this.aviaUi.accIndicatorState = {
      displayTime: this.accIndicatorDisplayTime,
      volumeKey: false,
      muted: false,
      seekForward: false,
      seekBack: false,
      seekTime: null
    };
    this.aviaUi.qualityLabel = (_a = this.options) === null || _a === void 0 ? void 0 : _a.qualityLabel;
    if ((_b = this.options) === null || _b === void 0 ? void 0 : _b.activeMode) {
      this.aviaUi.activeMode = this.options.activeMode;
    }
    if ((_c = this.options) === null || _c === void 0 ? void 0 : _c.previewTimeTransform) {
      this.previewTimeTransform = (_d = this.options) === null || _d === void 0 ? void 0 : _d.previewTimeTransform;
    }
    this.aviaUi.isTouch = 'ontouchstart' in document.documentElement;
    this.aviaUi.initialized = true;
  }
  setLocalizationData(lang) {
    const localeData = Object.values(LocalizationId).reduce((acc, id) => {
      acc[id] = this.services.localization.localize(id, lang);
      return acc;
    }, {});
    this.aviaUi.localeData = localeData;
  }
  getEventInterests() {
    const pe = this.avia.PlayerEvent;
    return Object.values(pe);
  }
  async initialize() {
    this.services.localization.registerLocalizationData(LocalizationData);
    await this.createUi();
  }
  applyTheme(theme = {}) {
    const container = this.getContainer();
    const { html, cssVars } = theme;
    let aviaUi = container.querySelector(this.rootElement);
    let previous;
    if (html && aviaUi) {
      previous = container.removeChild(aviaUi);
      previous.removeEventListener(UiEvents.ACTION, this.hUiAction);
      aviaUi = null;
      this.aviaUi = null;
    }
    if (!aviaUi) {
      const elements = (html) ? createElementsFromString(html) : [document.createElement(this.rootElement)];
      aviaUi = elements.find(element => element.nodeName.toLowerCase() === this.rootElement);
      if (!aviaUi) {
        throw new Error(`Avia UI theme HTML must contain the root element ${this.rootElement}`);
      }
      elements.forEach(element => container.appendChild(element));
      if (previous) {
        // @ts-ignore
        Object.keys(Object.getPrototypeOf(previous)).forEach(key => {
          const value = previous[key];
          if (typeof value === 'function') {
            return;
          }
          aviaUi[key] = previous[key];
        });
      }
    }
    if (!this.aviaUi) {
      this.aviaUi = aviaUi;
      this.aviaUi.addEventListener(UiEvents.ACTION, this.hUiAction);
    }
    if (cssVars) {
      applyCssVars(cssVars);
    }
  }
}

const createUi = AviaUiPlugin.create;
const create = AviaUiPlugin.create;

const aviaAccessibilityCss = "avia-accessibility{display:block}";

let AviaAccessibility$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  render() {
    return (h(Host, null, h("avia-volume-indicator", null), h("avia-seek-indicator", null)));
  }
  static get style() { return aviaAccessibilityCss; }
};

const aviaActiveCss = "avia-active{display:block;transition:var(--avia-active-transition-in)}avia-ui:not([active]) avia-active{opacity:0;transition:var(--avia-active-transition-out)}";

let AviaActive$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  render() {
    return (h(Host, null, h("slot", null)));
  }
  static get style() { return aviaActiveCss; }
};

const aviaAdControlBarCss = "avia-ad-control-bar{position:absolute;height:56px;width:100%;left:0;bottom:0;right:0;pointer-events:none;flex-direction:row;justify-content:center;display:none;transition:var(--avia-active-transition-in)}avia-ui[control-mode=\"ad\"] avia-ad-control-bar{display:flex}.avia-ad-control-container{pointer-events:auto;display:flex;width:calc(3 * var(--avia-control-bar-btn-width) + (2 * var(--avia-default-spacing)));height:var(--avia-control-bar-btn-height);justify-content:space-between;background-color:var(--avia-ad-control-bg-color);border-radius:var(--avia-ad-control-border-radius)}avia-ui:not([active]) avia-ad-control-bar{opacity:0;transition:var(--avia-active-transition-out)}";

let AviaAdControlBar$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  render() {
    return (h(Host, null, h("div", { class: "avia-ad-control-container" }, h("avia-mute-toggle", null), h("avia-play-toggle", null), h("avia-fullscreen-toggle", null)), h("avia-ad-progress-bar", null)));
  }
  static get style() { return aviaAdControlBarCss; }
};

var createDeferredPromise = function () {
    var resolve;
    var promise = new Promise(function (res) { resolve = res; });
    return { promise: promise, resolve: resolve };
};

var openWormhole = function (Component, props, isBlocking) {
    if (isBlocking === void 0) { isBlocking = true; }
    var isConstructor = (Component.constructor.name === 'Function');
    var Proto = isConstructor ? Component.prototype : Component;
    var componentWillLoad = Proto.componentWillLoad;
    Proto.componentWillLoad = function () {
        var _this = this;
        var el = getElement(this);
        var onOpen = createDeferredPromise();
        var event = new CustomEvent('openWormhole', {
            bubbles: true,
            composed: true,
            detail: {
                consumer: this,
                fields: props,
                updater: function (prop, value) {
                    var target = (prop in el) ? el : _this;
                    target[prop] = value;
                },
                onOpen: onOpen,
            },
        });
        el.dispatchEvent(event);
        var willLoad = function () {
            if (componentWillLoad) {
                return componentWillLoad.call(_this);
            }
        };
        return isBlocking ? onOpen.promise.then(function () { return willLoad(); }) : (willLoad());
    };
};

var multiverse = new Map();
var updateConsumer = function (_a, state) {
    var fields = _a.fields, updater = _a.updater;
    fields.forEach(function (field) { updater(field, state[field]); });
};
var Universe = {
    create: function (creator, initialState) {
        var el = getElement(creator);
        var wormholes = new Map();
        var universe = { wormholes: wormholes, state: initialState };
        multiverse.set(creator, universe);
        var connectedCallback = creator.connectedCallback;
        creator.connectedCallback = function () {
            multiverse.set(creator, universe);
            if (connectedCallback) {
                connectedCallback.call(creator);
            }
        };
        var disconnectedCallback = creator.disconnectedCallback;
        creator.disconnectedCallback = function () {
            multiverse.delete(creator);
            if (disconnectedCallback) {
                disconnectedCallback.call(creator);
            }
        };
        el.addEventListener('openWormhole', function (event) {
            event.stopPropagation();
            var _a = event.detail, consumer = _a.consumer, onOpen = _a.onOpen;
            if (wormholes.has(consumer))
                return;
            if (typeof consumer !== 'symbol') {
                var connectedCallback_1 = consumer.connectedCallback, disconnectedCallback_1 = consumer.disconnectedCallback;
                consumer.connectedCallback = function () {
                    wormholes.set(consumer, event.detail);
                    if (connectedCallback_1) {
                        connectedCallback_1.call(consumer);
                    }
                };
                consumer.disconnectedCallback = function () {
                    wormholes.delete(consumer);
                    if (disconnectedCallback_1) {
                        disconnectedCallback_1.call(consumer);
                    }
                };
            }
            wormholes.set(consumer, event.detail);
            updateConsumer(event.detail, universe.state);
            onOpen === null || onOpen === void 0 ? void 0 : onOpen.resolve(function () { wormholes.delete(consumer); });
        });
        el.addEventListener('closeWormhole', function (event) {
            var consumer = event.detail;
            wormholes.delete(consumer);
        });
    },
    Provider: function (_a, children) {
        var state = _a.state;
        var creator = getRenderingRef();
        if (multiverse.has(creator)) {
            var universe = multiverse.get(creator);
            universe.state = state;
            universe.wormholes.forEach(function (opening) { updateConsumer(opening, state); });
        }
        return children;
    }
};

const aviaAdProgressBarCss = "avia-ad-progress-bar{display:block;position:absolute;background:var(--avia-ad-progress-track-color);position:absolute;height:3px;bottom:0;left:0;right:0;user-select:none}.avia-ad-prog-bar{height:3px;user-select:none;background-color:var(--avia-ad-progress-color);width:0%}";

let AviaAdProgressBar$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.adPlayheadPosition = 0;
    this.isLiveLinear = false;
  }
  adPlayheadPositionHandler(val) {
    if (!this.pbar || val === null || val === undefined || isNaN(val) || val < 0)
      return;
    this.pbar.style.width = `${val * 100}%`;
  }
  render() {
    return (h("div", { class: 'avia-ad-prog-bar', ref: (el) => this.pbar = el }));
  }
  static get watchers() { return {
    "adPlayheadPosition": ["adPlayheadPositionHandler"]
  }; }
  static get style() { return aviaAdProgressBarCss; }
};
openWormhole(AviaAdProgressBar$1, ['adPlayheadPosition', 'isLiveLinear']);

const aviaButtonCss = "avia-button{display:inline-block;background-color:transparent;height:var(--avia-control-bar-btn-height);width:var(--avia-control-bar-btn-width);color:var(--avia-control-bar-btn-text-color);border:var(--avia-control-bar-btn-border);border-width:var(--avia-control-bar-btn-border-width);border-radius:var(--avia-control-bar-btn-border-radius);outline:var(--avia-control-bar-btn-border-outline);cursor:var(--avia-control-bar-btn-border-cursor);pointer-events:all}avia-button:hover{color:var(--avia-control-bar-btn-text-color-hover)}avia-button[disabled]{cursor:var(--avia-control-bar-btn-disabled-cursor);opacity:var(--avia-control-bar-btn-disabled-opacity);pointer-events:none}avia-button[icon]{padding:0;margin:0;background-position:center;background-size:var(--avia-icon-btn-bg-size);background-repeat:no-repeat;user-select:none}avia-button[compact]{width:var(--avia-compact-button-width);height:var(--avia-compact-button-height);border-radius:var(--avia-compact-button-border-radius);background-color:var(--avia-compact-button-background-color)}";

let AviaButton$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.actionEvent = createEvent(this, "action", 7);
    this.toggled = false;
    this.compact = false;
    this.icon = false;
    this.variant = false;
    this.actions = '';
    this.ids = '';
    this.disabled = false;
    this.focused = false;
    this.TAP_THRESHOLD = 300;
    this.isTouch = 'ontouchstart' in document.documentElement;
  }
  onClick(event) {
    if (this.isTouch) {
      blockEvent(event);
      return;
    }
    this.execAction();
  }
  onTouchStart(event) {
    blockEvent(event);
    this.touchStartStamp = event.timeStamp;
  }
  onTouchEnd(event) {
    if (this.touchStartStamp && ((event.timeStamp - this.touchStartStamp) <= this.TAP_THRESHOLD)) {
      this.touchStartStamp = null;
      this.execAction();
    }
  }
  onFocus() {
    if (!this.element.classList.contains("focus-visible")) {
      return;
    }
    this.focused = true;
  }
  onBlur() {
    if (!this.focused) {
      return;
    }
    this.focused = false;
  }
  onKeyDown(event) {
    switch (event.key) {
      case "Enter":
      case " ":
        this.element.click();
        blockEvent(event);
        return;
    }
  }
  getCsvValue(state, index = +this.toggled) {
    return csvToArray(state)[index];
  }
  execAction() {
    const action = this.getCsvValue(this.actions);
    if (!action) {
      return;
    }
    this.actionEvent.emit({ action });
  }
  render() {
    var _a;
    const id = this.getCsvValue(this.ids) || this.locId;
    const ariaLabel = (_a = this.localeData) === null || _a === void 0 ? void 0 : _a[id];
    const attributes = {
      'aria-label': ariaLabel,
    };
    if (!this.focused && ariaLabel) {
      attributes.title = ariaLabel;
    }
    return (h(Host, Object.assign({ disabled: this.disabled, tabIndex: 0 }, attributes), h("slot", null)));
  }
  get element() { return this; }
  static get style() { return aviaButtonCss; }
};
openWormhole(AviaButton$1, ['localeData']);

const aviaControlBarCss = "avia-control-bar{box-sizing:border-box;position:absolute;bottom:0;left:0;width:100%;height:var(--avia-control-bar-height);padding:0 var(--avia-default-spacing);background:var(--avia-control-bar-bg);display:none;transition:var(--avia-active-transition-in)}avia-control-bar.avia-live-linear{padding-top:var(--avia-live-padding-top)}avia-ui[control-mode=\"content\"] avia-control-bar{display:block}avia-ui:not([active]) avia-control-bar{opacity:0;transition:var(--avia-active-transition-out)}.avia-control-container{display:flex;justify-content:space-between;align-items:center;height:var(--avia-control-bar-btn-height);flex-wrap:nowrap}.avia-control-container avia-button{height:var(--avia-control-bar-btn-height)}avia-control-bar.avia-layout-c-30 .avia-control-group-left,avia-control-bar.avia-layout-c-30 .avia-control-group-right{width:35%}avia-control-bar.avia-layout-c-30 .avia-control-group-center{width:30%}avia-control-bar.avia-layout-c-40 .avia-control-group-left,avia-control-bar.avia-layout-c-40 .avia-control-group-right{width:30%}avia-control-bar.avia-layout-c-40 .avia-control-group-center{width:40%}avia-control-bar.avia-layout-c-50 .avia-control-group-left,avia-control-bar.avia-layout-c-50 .avia-control-group-right{width:25%}avia-control-bar.avia-layout-c-50 .avia-control-group-center{width:50%}avia-control-bar.avia-layout-c-60 .avia-control-group-left,avia-control-bar.avia-layout-c-60 .avia-control-group-right{width:20%}avia-control-bar.avia-layout-c-60 .avia-control-group-center{width:60%}avia-control-bar.avia-layout-c-70 .avia-control-group-left,avia-control-bar.avia-layout-c-70 .avia-control-group-right{width:15%}avia-control-bar.avia-layout-c-70 .avia-control-group-center{width:70%}avia-control-bar[avia-layout=default] .avia-control-group-left,avia-control-bar[avia-layout=default] .avia-control-group-right{display:flex;height:var(--avia-control-bar-btn-height);flex-basis:auto}avia-control-bar[avia-layout=default] .avia-control-group-left{flex-grow:1}.avia-control-group-left,.avia-control-group-right,.avia-control-group-center,.avia-control-group-left>*,.avia-control-group-right>*,.avia-control-group-center>*{display:flex;height:var(--avia-control-bar-btn-height);flex-basis:auto;align-items:center}.avia-control-group-center>*{flex-grow:1}.avia-control-group-right,.avia-control-group-right>*{justify-content:flex-end}.avia-control-group-left>*{justify-content:flex-start}.avia-control-group-center>*{justify-content:space-evenly}.avia-control-group-left>*>*{margin-right:var(--avia-half-spacing)}.avia-control-group-right>*>*{margin-left:var(--avia-half-spacing)}avia-control-bar>avia-progress-seek:empty{display:none}.avia-control-group-left>*:empty,.avia-control-group-right>*:empty,.avia-control-group-center>*:empty{display:none}";

let AviaControlBar$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.isLiveLinear = false;
    this.controlMode = ControlMode.NONE;
  }
  async getHeight() {
    return this.el.getBoundingClientRect().height;
  }
  render() {
    return (h(Host, { class: this.isLiveLinear ? 'avia-live-linear' : '' }, h("slot", { name: "top" }, !this.isLiveLinear ? h("avia-progress-bar", null) : ''), h("div", { class: "avia-control-container" }, h("div", { class: "avia-control-group-left" }, h("slot", { name: "left" }, h("avia-play-toggle", null), h("avia-mute-toggle", null), h("avia-volume-slider", null))), h("div", { class: "avia-control-group-center" }, h("slot", { name: "center" })), h("div", { class: "avia-control-group-right" }, h("slot", { name: "right" }, h("avia-time-display", null), h("avia-live-control", null), h("avia-text-toggle", null), h("avia-settings-toggle", null), h("avia-fullscreen-toggle", null))))));
  }
  get el() { return this; }
  static get style() { return aviaControlBarCss; }
};
openWormhole(AviaControlBar$1, ['isLiveLinear', 'controlMode']);

const aviaControlBarCompactCss = "avia-control-bar-compact{display:none;position:absolute;top:50%;left:50%;width:var(--avia-compact-control-bar-width);height:var(--avia-compact-button-height);border-radius:var(--avia-compact-button-border-radius);margin-left:var(--avia-compact-control-bar-margin-left);margin-top:var(--avia-compact-control-bar-margin-top)}avia-control-bar-compact avia-mute-toggle,avia-control-bar-compact avia-play-toggle{margin-right:var(--avia-default-spacing)}avia-ui[control-mode=\"compact\"][active] avia-control-bar-compact,avia-ui[control-mode=\"compact\"][active] avia-control-bar-compact>*{display:block}";

let AviaControlBarCompact$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  render() {
    return (h(Host, null, h("slot", null, h("avia-mute-toggle", { compact: true }), h("avia-play-toggle", { compact: true }), h("avia-fullscreen-toggle", { compact: true }))));
  }
  static get style() { return aviaControlBarCompactCss; }
};

const aviaCurrentTimeCss = ":host{display:inline-block}.avia-current-time{display:inline-block;user-select:none;height:var(--avia-time-height);line-height:var(--avia-time-line-height);font-size:var(--avia-time-font-size);color:var(--avia-time-color);font-family:var(--avia-time-font-family)}.avia-control-group-right .avia-current-time{text-align:right}";

let AviaCurrentTime$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.isLiveLinear = false;
    this.isDvr = false;
  }
  render() {
    const inactive = this.isLiveLinear || this.isDvr;
    if (inactive) {
      return '';
    }
    const t = this.currentTimeSeconds || 0;
    const lbl = this.localeData ? createLocalizedTimeString(t, this.localeData) : '';
    return (h("div", { class: 'avia-current-time', "aria-label": lbl, title: lbl }, secToHms(t)));
  }
  static get style() { return aviaCurrentTimeCss; }
};
openWormhole(AviaCurrentTime$1, ['currentTimeSeconds', 'localeData', 'isLiveLinear', 'isDvr']);

const aviaDurationCss = ":host{display:inline-block}.avia-duration{display:inline-block;user-select:none;height:var(--avia-time-height);line-height:var(--avia-time-line-height);font-size:var(--avia-time-font-size);color:var(--avia-time-color);font-family:var(--avia-time-font-family)}.avia-control-group-right .avia-duration{text-align:right}";

let AviaDuration$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.isLiveLinear = false;
    this.isDvr = false;
  }
  render() {
    const inactive = this.isLiveLinear || this.isDvr;
    if (inactive) {
      return '';
    }
    const t = this.durationSeconds || 0;
    const lbl = this.localeData ? createLocalizedTimeString(t, this.localeData) : '';
    return (h("div", { class: 'avia-duration', "aria-label": lbl, title: lbl }, secToHms(this.durationSeconds || 0)));
  }
  static get style() { return aviaDurationCss; }
};
openWormhole(AviaDuration$1, ['durationSeconds', 'localeData', 'isLiveLinear', 'isDvr']);

const aviaFullscreenToggleCss = "avia-fullscreen-toggle{display:inline-block}avia-fullscreen-toggle avia-button{background-image:var(--avia-fs-icon)}avia-fullscreen-toggle avia-button:hover{background-image:var(--avia-fs-icon-hover)}avia-fullscreen-toggle avia-button[toggled]{background-image:var(--avia-fs-exit-icon)}avia-fullscreen-toggle avia-button[toggled]:hover{background-image:var(--avia-fs-exit-icon-hover)}";

let AviaFullscreenToggle$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.fullscreen = false;
    this.fullscreenDisabled = true;
    this.compact = false;
  }
  render() {
    return (h("avia-button", { ids: `${LocalizationId.FULL_SCREEN_ENTER}, ${LocalizationId.FULL_SCREEN_EXIT}`, actions: `${Action.ENTER_FULLSCREEN}, ${Action.EXIT_FULLSCREEN}`, disabled: this.fullscreenDisabled, toggled: this.fullscreen, compact: this.compact, icon: true }));
  }
  static get style() { return aviaFullscreenToggleCss; }
};
openWormhole(AviaFullscreenToggle$1, ['fullscreen', 'fullscreenDisabled']);

const aviaLiveControlCss = "avia-live-control{display:inline-block;margin-right:var(--avia-default-spacing)}avia-live-control avia-button{width:auto;height:auto !important;font-size:0.9em;text-align:right;overflow:hidden}avia-live-control avia-button[disabled]{opacity:1;cursor:default}avia-live-control avia-button::before,avia-live-control avia-button[disabled]::before{content:'• ';color:red;font-size:0.9em}";

let AviaLiveControl$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.isLiveLinear = false;
    this.isDvr = false;
    this.live = false;
  }
  render() {
    if (!(this.isLiveLinear || this.isDvr)) {
      return;
    }
    const isLive = this.live || this.isLiveLinear;
    const locId = isLive ? LocalizationId.LIVE : LocalizationId.GO_LIVE;
    return (h("avia-button", { actions: `${Action.NONE}, ${Action.GO_TO_LIVE}`, toggled: this.live, disabled: isLive }, h("avia-text", { "loc-id": locId })));
  }
  static get style() { return aviaLiveControlCss; }
};
openWormhole(AviaLiveControl$1, ['isLiveLinear', 'live', 'isDvr']);

const aviaMuteToggleCss = "avia-mute-toggle{display:inline-block}avia-mute-toggle avia-button{background-image:var(--avia-unmuted-icon)}avia-mute-toggle avia-button:hover{background-image:var(--avia-unmuted-icon-hover)}avia-mute-toggle avia-button[toggled]{background-image:var(--avia-mute-icon)}avia-mute-toggle avia-button[toggled]:hover{background-image:var(--avia-mute-icon-hover)}";

let AviaMuteToggle$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.muted = false;
    this.muteDisabled = true;
    this.compact = false;
  }
  render() {
    return (h("avia-button", { ids: `${LocalizationId.MUTE}, ${LocalizationId.UNMUTE}`, actions: `${Action.MUTE}, ${Action.UNMUTE}`, toggled: this.muted, disabled: this.muteDisabled, compact: this.compact, icon: true }));
  }
  static get style() { return aviaMuteToggleCss; }
};
openWormhole(AviaMuteToggle$1, ['muted', 'muteDisabled']);

const aviaOverlayCss = "avia-overlay{background:transparent;position:absolute;width:100%;height:100%;top:0;left:0;border:none;padding:0;margin:0}avia-overlay[centered]{justify-content:center;align-items:center;display:flex;cursor:pointer}";

let AviaOverlay$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.centered = false;
  }
  render() {
    return (h(Host, { centered: this.centered }, h("slot", null)));
  }
  static get style() { return aviaOverlayCss; }
};

const aviaPlayCtaCss = "avia-play-cta avia-button{width:var(--avia-play-cta-width);height:var(--avia-play-cta-height);border-radius:var(--avia-play-cta-border-radius);background-image:var(--avia-play-cta-bg-image);background-color:var(--avia-play-cta-bg-color);background-repeat:no-repeat;background-position-x:calc(var(--avia-play-cta-bg-position) + 6%);background-position-y:var(--avia-play-cta-bg-position);background-size:var(--avia-play-cta-bg-size);cursor:pointer}";

let AviaPlayCta$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  render() {
    return (h("avia-button", { disabled: false, "loc-id": LocalizationId.PLAY }));
  }
  static get style() { return aviaPlayCtaCss; }
};

const aviaPlayOverlayCss = "avia-play-overlay{background-color:var(--avia-play-overlay-bg-color)}avia-play-overlay.focus-visible avia-play-cta{box-shadow:var(--avia-focus-box-shadow-inner)}";

let AviaPlayOverlay$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.actionEvent = createEvent(this, "action", 7);
    this.playing = false;
    this.controlMode = ControlMode.NONE;
    this.playDisabled = true;
    this.pausable = true;
    this.isTouch = false;
  }
  onClick() {
    const action = this.playing ? Action.PAUSE : Action.PLAY;
    this.actionEvent.emit({ action });
  }
  render() {
    const paused = !this.playing;
    const noTouch = !paused && this.isTouch;
    if (noTouch || this.playDisabled || !this.pausable || this.controlMode !== ControlMode.CONTENT) {
      return;
    }
    return (h("avia-overlay", { centered: true }, paused && h("avia-play-cta", null)));
  }
  static get style() { return aviaPlayOverlayCss; }
};
openWormhole(AviaPlayOverlay$1, ['playing', 'playDisabled', 'pausable', 'controlMode', 'isTouch']);

const aviaPlayToggleCss = "avia-play-toggle{display:inline-block}avia-play-toggle avia-button{background-image:var(--avia-play-icon)}avia-play-toggle avia-button:hover{background-image:var(--avia-play-icon-hover)}avia-play-toggle avia-button[toggled]{background-image:var(--avia-pause-icon)}avia-play-toggle avia-button[toggled]:hover{background-image:var(--avia-pause-icon-hover)}avia-play-toggle avia-button[toggled][variant]{background-image:var(--avia-stop-icon)}avia-play-toggle avia-button[toggled][variant]:hover{background-image:var(--avia-stop-icon-hover)}";

let AviaPlayToggle$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.playing = false;
    this.playDisabled = true;
    this.compact = false;
  }
  render() {
    const paused = !this.playing;
    const ids = [LocalizationId.PLAY, this.pausable ? LocalizationId.PAUSE : LocalizationId.STOP];
    const actions = [Action.PLAY, this.pausable ? Action.PAUSE : Action.STOP];
    const locId = paused ? this.pausable ? LocalizationId.PAUSE : LocalizationId.STOP : LocalizationId.PLAY;
    return (h("avia-button", { ids: ids.join(','), actions: actions.join(','), disabled: this.playDisabled, "loc-id": locId, toggled: this.playing, compact: this.compact, variant: !this.pausable, icon: true }));
  }
  static get style() { return aviaPlayToggleCss; }
};
openWormhole(AviaPlayToggle$1, ['playing', 'playDisabled', 'pausable']);

const aviaPosterCss = "avia-poster img{width:100%;height:auto}";

let AviaPoster$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.actionEvent = createEvent(this, "action", 7);
    this.posterActive = false;
  }
  handleAction(event) {
    this.actionEvent.emit({ action: Action.POSTER_PLAY });
    blockEvent(event);
  }
  render() {
    if (!this.posterActive) {
      return;
    }
    return (h("avia-overlay", null, !isEmpty(this.posterImageUrl) ? h("img", { src: this.posterImageUrl }) : '', h("div", { class: "" }, h("slot", null)), h("avia-overlay", { centered: true }, h("avia-play-cta", null))));
  }
  static get style() { return aviaPosterCss; }
};
openWormhole(AviaPoster$1, ['posterActive', 'posterImageUrl']);

const aviaProgressBarCss = ":root{--avia-progress-preview-height:24px;--avia-progress-preview-width:50px;--avia-progress-preview-line-height:24px;--avia-progress-preview-top:-34px;--avia-progress-preview-bg-color:rgba(44, 44, 44, 0.7);--avia-progress-preview-color:rgb(220, 220, 220);--avia-progress-preview-font-size:13px;--avia-progress-preview-border-radius:4px}.avia-progress-preview{position:absolute;line-height:var(--avia-progress-preview-line-height);background:var(--avia-progress-preview-bg-color);color:var(--avia-progress-preview-color);font-size:var(--avia-progress-preview-font-size);text-align:center;border-radius:var(--avia-progress-preview-border-radius);user-select:none;overflow:hidden}.avia-progress-text{padding:0 0.5em 0 0.5em;white-space:nowrap}";

let AviaProgressBar$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.actionEvent = createEvent(this, "action", 7);
    this.increment = 15;
    this.hoverDelay = 250;
    this.hovering = false;
    this.cursor = 0;
    this.imgElement = document.createElement('img');
    this.dragging = false;
  }
  onMouseEnter(event) {
    clearTimeout(this.hoverTimeoutHandle);
    this.hoverTimeoutHandle = setTimeout(() => {
      this.hovering = true;
      this.onMouseMove(event);
    }, this.hoverDelay);
  }
  onMouseLeave() {
    clearTimeout(this.hoverTimeoutHandle);
    this.hovering = false;
  }
  onMouseMove(event) {
    if (!this.active) {
      return;
    }
    const offset = this.sliderElement.getBoundingClientRect();
    const pos = getEventPos(event).x - offset.left;
    const percent = clampValue(pos / offset.width, 0, 1);
    this.actionEvent.emit({ action: 'preview', value: { time: percent * this.durationSeconds, percent } });
    this.cursor = clampValue(pos, 0, offset.width);
  }
  get active() {
    return this.hovering || this.dragging;
  }
  onChange(e) {
    this.actionEvent.emit({ action: 'seek', value: e.detail.value });
  }
  onDragging(e) {
    this.dragging = e.detail.value;
  }
  componentDidRender() {
    if (!this.preview) {
      return;
    }
    const data = this.preview.data;
    const slider = this.sliderElement;
    const parentWidth = slider.offsetWidth;
    if (data) {
      const img = this.imgElement;
      if (img.src != data.url) {
        img.src = data.url;
      }
      const canvas = this.canvasElement;
      // TODO: Make this configurable
      const thumbnailToViewportScale = 0.25;
      const scale = Math.min((parentWidth * thumbnailToViewportScale) / data.width, 1);
      const context = canvas.getContext('2d');
      const width = data.width * scale;
      const height = data.height * scale;
      canvas.width = width;
      canvas.height = height;
      context.drawImage(img, data.x, data.y, data.width, data.height, 0, 0, width, height);
    }
    const preview = this.previewElement;
    const margin = preview.offsetWidth / 2;
    const max = parentWidth - margin;
    const offset = (this.cursor < margin) ? margin - this.cursor : (this.cursor > max) ? max - this.cursor : 0;
    preview.style.marginLeft = `${offset - margin}px`;
    preview.style.top = `-${preview.offsetHeight + slider.offsetHeight / 2}px`;
  }
  render() {
    var _a, _b;
    const ariaValueText = (this.localeData) ? valueOfTotal(this.currentTimeSeconds, this.durationSeconds, this.localeData) : '';
    const list = csvToArray(this.segmentBreakPositions).map(i => ({ value: parseFloat(i) }));
    return (h("avia-slider", { ref: e => this.sliderElement = e, "loc-id": LocalizationId.SEEK, "aria-valuetext": ariaValueText, list: list, value: this.currentTimeSeconds, max: this.durationSeconds, increment: this.increment, onChange: (e) => this.onChange(e), onDragging: (e) => this.onDragging(e) }, h("div", { ref: e => this.previewElement = e, class: "avia-progress-preview", style: {
        visibility: this.active ? "visible" : "hidden",
        left: `${this.cursor}px`,
      } }, h("canvas", { ref: e => this.canvasElement = e, class: "avia-progress-img", style: { display: ((_a = this.preview) === null || _a === void 0 ? void 0 : _a.data) ? "block" : "none" } }), h("div", { ref: e => this.textElement = e, class: "avia-progress-text" }, (_b = this.preview) === null || _b === void 0 ? void 0 : _b.value))));
  }
  static get style() { return aviaProgressBarCss; }
};
openWormhole(AviaProgressBar$1, [
  'currentTimeSeconds',
  'durationSeconds',
  'localeData',
  'seekDisabled',
  'segmentBreakPositions',
  'preview'
]);

const aviaSeekBackButtonCss = "avia-seek-back-button{display:inline-block}avia-seek-back-button avia-button{background-image:var(--avia-seek-back-icon)}avia-seek-back-button avia-button:hover{background-image:var(--avia-seek-back-icon-hover)}";

let AviaSeekBackButton$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.seekBackDisabled = false;
    this.isLiveLinear = false;
  }
  render() {
    if (this.isLiveLinear) {
      return '';
    }
    return (h("avia-button", { actions: Action.SEEK_BACK, disabled: this.seekBackDisabled, "loc-id": LocalizationId.SEEK_BACK, icon: true }));
  }
  static get style() { return aviaSeekBackButtonCss; }
};
openWormhole(AviaSeekBackButton$1, ['seekBackDisabled', 'isLiveLinear']);

const aviaSeekForwardButtonCss = "avia-seek-forward-button{display:inline-block}avia-seek-forward-button avia-button{background-image:var(--avia-seek-forward-icon)}avia-seek-forward-button avia-button:hover{background-image:var(--avia-seek-forward-icon-hover)}";

let AviaSeekForwardButton$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.seekForwardDisabled = false;
    this.isLiveLinear = false;
  }
  render() {
    if (this.isLiveLinear) {
      return '';
    }
    return (h("avia-button", { actions: Action.SEEK_FORWARD, disabled: this.seekForwardDisabled, "loc-id": LocalizationId.SEEK_FORWARD, icon: true }));
  }
  static get style() { return aviaSeekForwardButtonCss; }
};
openWormhole(AviaSeekForwardButton$1, ['seekForwardDisabled', 'isLiveLinear']);

const aviaSeekIndicatorCss = "avia-seek-indicator{position:absolute;display:inline-block;box-sizing:border-box;border-radius:var(--avia-seek-indicator-border-radius);background-color:var(--avia-seek-indicator-background-color);color:var(--avia-seek-indicator-color);height:var(--avia-seek-indicator-height);font-size:var(--avia-seek-indicator-font-size);font-family:var(--avia-seek-indicator-font-family);line-height:var(--avia-seek-indicator-line-height);top:calc(50% - 24px);opacity:0;transition-property:opacity;transition-duration:300ms;transition-timing-function:ease-in}avia-seek-indicator.avia-indicator-active{opacity:1}avia-seek-indicator:empty{opacity:0}.avia-acc-fwd{right:12px;padding-left:48px;padding-right:12px;text-align:right}.avia-acc-back{left:12px;padding-right:48px;padding-left:12px}avia-seek-indicator .avia-acc-arrow{width:24px;height:18px;position:absolute;top:calc(50% - 9px);background-image:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2017.81%2024%22%3E%3Cdefs%3E%3Cstyle%3E.cls-1%7Bfill%3A%23fff%3B%7D%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22Layer_2%22%20data-name%3D%22Layer%202%22%3E%3Cg%20id%3D%22Layer_2-2%22%20data-name%3D%22Layer%202%22%3E%3Cpath%20class%3D%22cls-1%22%20d%3D%22M0%2C6.55V17.79a.54.54%2C0%2C0%2C0%2C.54.54l5.15-.08a.54.54%2C0%2C0%2C1%2C.54.54v4.67a.54.54%2C0%2C0%2C0%2C.92.38l10.51-11.2a.56.56%2C0%2C0%2C0%2C0-.76L7.25.17a.54.54%2C0%2C0%2C0-.93.37V5.39a.54.54%2C0%2C0%2C1-.54.54L.54%2C6A.54.54%2C0%2C0%2C0%2C0%2C6.55Z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E');background-repeat:no-repeat;background-size:24px 18px}.avia-acc-fwd .avia-acc-arrow{left:12px}.avia-acc-back .avia-acc-arrow{transform:rotate(180deg);right:12px}";

let AviaSeekIndicator$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  componentDidRender() {
    if (!this.debouncer && this.accIndicatorState.displayTime > 0) {
      this.debouncer = debounce(() => {
        var _a;
        (_a = this.el) === null || _a === void 0 ? void 0 : _a.classList.remove(CssNames.INDICATOR_ACTIVE);
        this.visible = false;
      }, this.accIndicatorState.displayTime);
    }
    if (this.visible) {
      setTimeout(() => this.el.classList.add(CssNames.INDICATOR_ACTIVE), 0);
      this.debouncer && this.debouncer();
    }
  }
  render() {
    const acc = this.accIndicatorState;
    if (!acc.seekForward && !acc.seekBack) {
      this.visible = false;
      return '';
    }
    this.visible = true;
    const t = this.accIndicatorState.seekTime;
    const d = this.durationSeconds;
    const hasD = d && !isNaN(d);
    let str = '';
    if (hasD) {
      const dur = secToHms(d);
      if (!isNaN(t) && t >= 0) {
        const p = secToHms(t);
        str = p + ' / ' + dur;
      }
    }
    return (h(Host, { class: acc.seekForward ? CssNames.ACC_FWD : CssNames.ACC_BACK }, h("span", null, str), h("div", { class: 'avia-acc-arrow' })));
  }
  get el() { return this; }
  static get style() { return aviaSeekIndicatorCss; }
};
openWormhole(AviaSeekIndicator$1, ['durationSeconds', 'accIndicatorState']);

var LanguageLabel;
(function (LanguageLabel) {
  LanguageLabel["AB"] = "Abkhaz";
  LanguageLabel["AE"] = "Avesta";
  LanguageLabel["AF"] = "Afrikaans";
  LanguageLabel["AK"] = "Akan";
  LanguageLabel["AM"] = "\u12A0\u121B\u122D\u129B";
  LanguageLabel["AN"] = "Aragon\u00E9s";
  LanguageLabel["AR"] = "\u0627\u0644\u0639\u0631\u0628\u064A\u0629";
  LanguageLabel["AV"] = "A\u0432\u0430\u0440 \u043C\u0430\u0446\u04C0";
  LanguageLabel["AY"] = "Aymar aru";
  LanguageLabel["AZ"] = "az\u0259rbaycan dili";
  LanguageLabel["BA"] = "\u0431\u0430\u0448\u04A1\u043E\u0440\u0442 \u0442\u0435\u043B\u0435";
  LanguageLabel["BE"] = "\u0431\u0435\u043B\u0430\u0440\u0443\u0441\u043A\u0430\u044F \u043C\u043E\u0432\u0430";
  LanguageLabel["BG"] = "\u0431\u044A\u043B\u0433\u0430\u0440\u0441\u043A\u0438 \u0435\u0437\u0438\u043A";
  LanguageLabel["BH"] = "\u092D\u094B\u091C\u092A\u0941\u0930\u0940";
  LanguageLabel["BI"] = "Bislama";
  LanguageLabel["BM"] = "Bamanankan";
  LanguageLabel["BN"] = "\u09AC\u09BE\u0982\u09B2\u09BE";
  LanguageLabel["BO"] = "\u0F56\u0F7C\u0F51\u0F0B\u0F61\u0F72\u0F42";
  LanguageLabel["BR"] = "Brezhoneg";
  LanguageLabel["BS"] = "Bosanski jezik";
  LanguageLabel["CA"] = "catal\u00E0";
  LanguageLabel["CE"] = "\u043D\u043E\u0445\u0447\u0438\u0439\u043D \u043C\u043E\u0442\u0442";
  LanguageLabel["CH"] = "Chamoru";
  LanguageLabel["CO"] = "Corsu";
  LanguageLabel["CR"] = "\u14C0\u1426\u1403\u152D\u140D\u140F\u1423";
  LanguageLabel["CS"] = "\u010De\u0161tina";
  LanguageLabel["CU"] = "\u0469\u0437\u044B\u043A\u044A \u0441\u043B\u043E\u0432\u0463\u043D\u044C\u0441\u043A\u044A";
  LanguageLabel["CV"] = "\u0447\u04D1\u0432\u0430\u0448 \u0447\u04D7\u043B\u0445\u0438";
  LanguageLabel["CY"] = "Cymraeg";
  LanguageLabel["DA"] = "Dansk";
  LanguageLabel["DE"] = "Deutsch";
  LanguageLabel["DZ"] = "\u0F62\u0FAB\u0F7C\u0F44\u0F0B\u0F41";
  LanguageLabel["EE"] = "E\u028Begbe";
  LanguageLabel["EL"] = "\u03B5\u03BB\u03BB\u03B7\u03BD\u03B9\u03BA\u03AC";
  LanguageLabel["EN"] = "English";
  LanguageLabel["EO"] = "Esperanto";
  LanguageLabel["ES"] = "Espa\u00F1ol";
  LanguageLabel["ET"] = "Eesti";
  LanguageLabel["EU"] = "Euskara";
  LanguageLabel["FA"] = "\u0641\u0627\u0631\u0633\u06CC";
  LanguageLabel["FF"] = "Fulfulde";
  LanguageLabel["FI"] = "Suomi";
  LanguageLabel["FJ"] = "Vosa Vakaviti";
  LanguageLabel["FO"] = "F\u00F8royskt";
  LanguageLabel["FR"] = "Fran\u00E7ais";
  LanguageLabel["FY"] = "Frysk";
  LanguageLabel["GA"] = "Gaeilge";
  LanguageLabel["GD"] = "G\u00E0idhlig";
  LanguageLabel["GL"] = "Galego";
  LanguageLabel["GN"] = "Ava\u00F1e'\u1EBD";
  LanguageLabel["GU"] = "\u0A97\u0AC1\u0A9C\u0AB0\u0ABE\u0AA4\u0AC0";
  LanguageLabel["GV"] = "Gaelg";
  LanguageLabel["HA"] = "(Hausa) \u0647\u064E\u0648\u064F\u0633\u064E";
  LanguageLabel["HE"] = "\u05E2\u05D1\u05E8\u05D9\u05EA";
  LanguageLabel["HI"] = "\u0939\u093F\u0928\u094D\u0926\u0940";
  LanguageLabel["HO"] = "Hiri Motu";
  LanguageLabel["HR"] = "hrvatski jezik";
  LanguageLabel["HT"] = "Krey\u00F2l ayisyen";
  LanguageLabel["HU"] = "Magyar";
  LanguageLabel["HY"] = "\u0540\u0561\u0575\u0565\u0580\u0565\u0576";
  LanguageLabel["HZ"] = "Otjiherero";
  LanguageLabel["IA"] = "Interlingua";
  LanguageLabel["ID"] = "Bahasa Indonesia";
  LanguageLabel["IE"] = "Interlingue";
  LanguageLabel["IG"] = "As\u1EE5s\u1EE5 Igbo";
  LanguageLabel["II"] = "\uA188\uA320\uA4BF Nuosuhxop";
  LanguageLabel["IK"] = "I\u00F1upiaq";
  LanguageLabel["IO"] = "Ido";
  LanguageLabel["IS"] = "\u00CDslenska";
  LanguageLabel["IT"] = "Italiano";
  LanguageLabel["IU"] = "\u1403\u14C4\u1483\u144E\u1450\u1466";
  LanguageLabel["JA"] = "\u65E5\u672C\u4EBA";
  LanguageLabel["JV"] = "Basa Jawa";
  LanguageLabel["KA"] = "\u10E5\u10D0\u10E0\u10D7\u10E3\u10DA\u10D8";
  LanguageLabel["KG"] = "Kikongo";
  LanguageLabel["KI"] = "G\u0129k\u0169y\u0169";
  LanguageLabel["KJ"] = "Kuanyama";
  LanguageLabel["KK"] = "\u049B\u0430\u0437\u0430\u049B \u0442\u0456\u043B\u0456";
  LanguageLabel["KL"] = "kalaallisut";
  LanguageLabel["KM"] = "\u1781\u17D2\u1798\u17C2\u179A";
  LanguageLabel["KN"] = "\u0C95\u0CA8\u0CCD\u0CA8\u0CA1";
  LanguageLabel["KO"] = "\uD55C\uAD6D\uC5B4";
  LanguageLabel["KR"] = "Kanuri";
  LanguageLabel["KS"] = "\u0915\u0936\u094D\u092E\u0940\u0930\u0940,";
  LanguageLabel["KU"] = "Kurd\u00EE\u200E";
  LanguageLabel["KV"] = "\u043A\u043E\u043C\u0438 \u043A\u044B\u0432";
  LanguageLabel["KW"] = "Kernewek";
  LanguageLabel["KY"] = "\u041A\u044B\u0440\u0433\u044B\u0437\u0447\u0430";
  LanguageLabel["LA"] = "Latine";
  LanguageLabel["LB"] = "L\u00EBtzebuergesch";
  LanguageLabel["LG"] = "Luganda";
  LanguageLabel["LI"] = "Limburgs";
  LanguageLabel["LN"] = "Ling\u00E1la";
  LanguageLabel["LO"] = "\u0E9E\u0EB2\u0EAA\u0EB2\u0EA5\u0EB2\u0EA7";
  LanguageLabel["LT"] = "Lietuvi\u0173 kalba";
  LanguageLabel["LU"] = "Kiluba";
  LanguageLabel["LV"] = "latvie\u0161u valoda";
  LanguageLabel["MG"] = "fiteny malagasy";
  LanguageLabel["MH"] = "Kajin M\u0327aje\u013C";
  LanguageLabel["MI"] = "te reo M\u0101ori";
  LanguageLabel["MK"] = "\u043C\u0430\u043A\u0435\u0434\u043E\u043D\u0441\u043A\u0438 \u0458\u0430\u0437\u0438\u043A";
  LanguageLabel["ML"] = "\u0D2E\u0D32\u0D2F\u0D3E\u0D33\u0D02";
  LanguageLabel["MN"] = "\u041C\u043E\u043D\u0433\u043E\u043B \u0445\u044D\u043B";
  LanguageLabel["MR"] = "\u092E\u0930\u093E\u0920\u0940";
  LanguageLabel["MS"] = "Bahasa Melayu\u200E";
  LanguageLabel["MT"] = "Malti";
  LanguageLabel["MY"] = "\u1017\u1019\u102C\u1005\u102C";
  LanguageLabel["NA"] = "Dorerin Naoero";
  LanguageLabel["NB"] = "Norsk Bokm\u00E5l";
  LanguageLabel["NE"] = "\u0928\u0947\u092A\u093E\u0932\u0940";
  LanguageLabel["NG"] = "Owambo";
  LanguageLabel["NL"] = "Nederlands";
  LanguageLabel["NN"] = "Norsk Nynorsk";
  LanguageLabel["NO"] = "Norsk";
  LanguageLabel["NR"] = "IsiNdebele";
  LanguageLabel["NV"] = "Din\u00E9 bizaad";
  LanguageLabel["NY"] = "ChiChe\u0175a";
  LanguageLabel["OC"] = "Occitan";
  LanguageLabel["OJ"] = "\u140A\u14C2\u1511\u14C8\u142F\u14A7\u140E\u14D0";
  LanguageLabel["OM"] = "Afaan Oromoo";
  LanguageLabel["OR"] = "\u0B13\u0B21\u0B3C\u0B3F\u0B06";
  LanguageLabel["OS"] = "\u0438\u0440\u043E\u043D \u00E6\u0432\u0437\u0430\u0433";
  LanguageLabel["PA"] = "\u0A2A\u0A70\u0A1C\u0A3E\u0A2C\u0A40";
  LanguageLabel["PI"] = "\u092A\u093E\u0932\u093F, \u092A\u093E\u0933\u093F";
  LanguageLabel["PL"] = "J\u0119zyk polski";
  LanguageLabel["PS"] = "\u067E\u069A\u062A\u0648";
  LanguageLabel["PT"] = "Portugu\u00EAs";
  LanguageLabel["QU"] = "Runa Simi";
  LanguageLabel["RM"] = "Rumantsch Grischun";
  LanguageLabel["RN"] = "Ikirundi";
  LanguageLabel["RO"] = "Rom\u00E2n\u0103";
  LanguageLabel["RU"] = "\u0440\u0443\u0441\u0441\u043A\u0438\u0439";
  LanguageLabel["RW"] = "Ikinyarwanda";
  LanguageLabel["SA"] = "\u0938\u0902\u0938\u094D\u0915\u0943\u0924\u092E\u094D";
  LanguageLabel["SC"] = "sardu";
  LanguageLabel["SD"] = "\u0938\u093F\u0928\u094D\u0927\u0940\u200E";
  LanguageLabel["SE"] = "Davvis\u00E1megiella";
  LanguageLabel["SG"] = "Y\u00E2ng\u00E2 t\u00EE s\u00E4ng\u00F6";
  LanguageLabel["SI"] = "\u0DC3\u0DD2\u0D82\u0DC4\u0DBD";
  LanguageLabel["SK"] = "Sloven\u010Dina";
  LanguageLabel["SL"] = "Slovenski jezik";
  LanguageLabel["SM"] = "Gagana fa'a Samoa";
  LanguageLabel["SN"] = "chiShona";
  LanguageLabel["SO"] = "Soomaaliga";
  LanguageLabel["SQ"] = "Shqip";
  LanguageLabel["SR"] = "\u0441\u0440\u043F\u0441\u043A\u0438 \u0458\u0435\u0437\u0438\u043A";
  LanguageLabel["SS"] = "SiSwati";
  LanguageLabel["ST"] = "Sesotho";
  LanguageLabel["SU"] = "Basa Sunda";
  LanguageLabel["SV"] = "Svenska";
  LanguageLabel["SW"] = "Kiswahili";
  LanguageLabel["TA"] = "\u0BA4\u0BAE\u0BBF\u0BB4\u0BCD";
  LanguageLabel["TE"] = "\u0C24\u0C46\u0C32\u0C41\u0C17\u0C41";
  LanguageLabel["TG"] = "\u0442\u043E\u04B7\u0438\u043A\u04E3\u200E";
  LanguageLabel["TH"] = "\u0E44\u0E17\u0E22";
  LanguageLabel["TI"] = "\u1275\u130D\u122D\u129B";
  LanguageLabel["TK"] = "T\u00FCrkmen";
  LanguageLabel["TL"] = "Wikang Tagalog";
  LanguageLabel["TN"] = "Setswana";
  LanguageLabel["TO"] = "Faka Tonga";
  LanguageLabel["TR"] = "T\u00FCrk\u00E7e";
  LanguageLabel["TS"] = "Xitsonga";
  LanguageLabel["TT"] = "\u0442\u0430\u0442\u0430\u0440 \u0442\u0435\u043B\u0435";
  LanguageLabel["TW"] = "Twi";
  LanguageLabel["TY"] = "Reo Tahiti";
  LanguageLabel["UG"] = "\u0626\u06C7\u064A\u063A\u06C7\u0631\u0686\u06D5\u200E";
  LanguageLabel["UK"] = "\u0423\u043A\u0440\u0430\u0457\u043D\u0441\u044C\u043A\u0430";
  LanguageLabel["UR"] = "\u0627\u0631\u062F\u0648";
  LanguageLabel["UZ"] = "O\u02BBzbek\u200E";
  LanguageLabel["VE"] = "Tshiven\u1E13a";
  LanguageLabel["VI"] = "Ti\u1EBFng Vi\u1EC7t";
  LanguageLabel["VO"] = "Volap\u00FCk";
  LanguageLabel["WA"] = "Walon";
  LanguageLabel["WO"] = "Wollof";
  LanguageLabel["XH"] = "IsiXhosa";
  LanguageLabel["YI"] = "\u05D9\u05D9\u05B4\u05D3\u05D9\u05E9";
  LanguageLabel["YO"] = "Yor\u00F9b\u00E1";
  LanguageLabel["ZA"] = "Sa\u026F cue\u014B\u0185";
  LanguageLabel["ZH"] = "\u4E2D\u6587 (Zh\u014Dngw\u00E9n)";
  LanguageLabel["ZU"] = "IsiZulu";
})(LanguageLabel || (LanguageLabel = {}));

const aviaSettingsCss = ":root{--avia-chevron-right:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22white%22%3E%3Cpath%20id%3D%22Arrow_2_1%22%20d%3D%22M0%2C24L14.4%2C11.556%2C0%2C0%2C6.74%2C0%2C24%2C11.556%2C6.74%2C24Z%22%2F%3E%3C%2Fsvg%3E');--avia-x-symbol:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2012%2012%22%20width%3D%2212%22%20height%3D%2212%22%3E%3Cstyle%3Etspan%20%7B%20white-space%3Apre%20%7D%3C%2Fstyle%3E%3Cpath%20fill%3D%22%23FFF%22%20d%3D%22M0.07%202.19L2.26%200L5.91%203.65L9.56%200L11.75%202.19L8.1%205.84L11.75%209.49L9.56%2011.68L5.91%208.03L2.26%2011.68L0.07%209.49L3.72%205.84L0.07%202.19Z%22%20%2F%3E%3C%2Fsvg%3E');--avia-menu-text-color:var(--avia-text-color);--avia-menu-text-color-hover:var(--avia-text-color-hover)}avia-settings{display:block;position:absolute;pointer-events:none}avia-settings>*{pointer-events:auto}.avia-settings-menu{min-width:180px;height:auto;background-color:rgba(0, 0, 0, 0.65);position:absolute;bottom:64px;right:var(--avia-default-spacing);display:flex;flex-direction:column;max-height:calc(100% - 1em - var(--avia-control-bar-height));border-radius:0.25em;overflow:hidden}avia-ui.avia-settings-nudge-left .avia-settings-menu{right:calc(var(--avia-default-spacing) + 48px)}.avia-settings-menu-header{padding:0.5em 0.25em 0.5em 0.75em;color:var(--avia-accent-color);background-color:black;border-bottom:1px solid #444;display:flex;align-items:center;justify-content:center;font-weight:bold}.avia-menu-button{background-color:transparent;border:none;outline:none;cursor:pointer;width:100%;text-align:left;color:var(--avia-menu-text-color);padding:1em;font-size:1em;background-position:right center;background-repeat:no-repeat;background-size:20% 20%;margin:0}.avia-menu-button:hover{color:var(--avia-menu-text-color-hover);background-color:rgba(0, 0, 0, 0.65)}.avia-menu-button[aria-haspopup=true]{background-image:var(--avia-chevron-right)}.avia-menu-button[disabled]{cursor:default;opacity:0.75}.avia-menu-button[disabled]:hover{background-color:transparent;color:var(--avia-menu-text-color)}.avia-menu-close{width:1.75em;height:2em;background-image:var(--avia-x-symbol);background-repeat:no-repeat;padding:0px}.avia-settings-back{width:1.75em;height:1.75em;background-image:var(--avia-chevron-right);background-repeat:no-repeat;background-size:100% 100%;transform:rotate(180deg);margin-right:1em}.avia-spacer{flex-grow:1}.avia-settings-menu.avia-language-menu{min-width:180px}.avia-menu-content{display:flex;flex-direction:row;overflow:hidden}.avia-menu-content>div{width:180px;color:var(--avia-menu-text-color)}.avia-menu-content>div:first-child{border-right:1px solid #888}.avia-menu-content>div:only-child{border-right:none}.avia-list{display:flex;flex-direction:column;font-size:0.95em}.avia-list-title{padding-left:1em;color:var(--avia-accent-color);font-size:0.9em;font-weight:bold;height:2em;line-height:2em;background-color:black;border-bottom:1px solid #444}.avia-list .avia-menu-button[aria-checked=true]{font-weight:bold;cursor:default;background-position:94% center;background-repeat:no-repeat;background-size:0.9em;background-image:var(--avia-selected-icon)}.avia-list .avia-list-container{overflow:scroll}.avia-settings-menu.avia-quality-menu{max-width:180px}.avia-menu-button[data-description]:after{content:url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAVCAYAAABc6S4mAAACG0lEQVQ4jZ2Vz0tVQRTHP89QKJ9lm6ikzN6zJAlxJ1E8CJO27oo2IdKmRdBWwRZhkH9A4kJoI7ppIUWkibRsERpILpQCF9lPLPpBQc+vTJ0Lh+lduc8Dw8z9zJlz5sw5MzcniR1IAegDDgJrwCjwvqKZ4CBDa5VUkrTf6YbNnZc0JemHpHFJtbGtrBF0AOeAI8AxYAp44OYvAo+BT0AJeFVtBEnbI2lI/+RZtOPDxsuSmnwEd4FG4E+06xxQa2c8DSy6uSKwYufeBnwxfgGYAZaBU0kEWeWJpONux822bjWK8qHx63+PX9J6FU5+SWp3xm4av+PYSWMh8XXhiNat3IIMA5+BGqAM1ANX7BgSCfotwG/T+wrkgV3AptMJNrviCNKSOxlF0ufm7hu77NiIsVs1UWJbUsr0EvDBffe48YL1JcdeW98bO9hO5lI28tb63Y6VrS9U48Dn4Z0b563fdCyX6MUONlKMXwU63fdzNy5Y/8axpGgexUnukLRPUqOkvZKKkgYqlOtRl9AlY82OLRvrjh38lPTdavhbyl245gy1G1t07IDTzWNvR1a5EZXviq1rdeyesZHkJr+QtLZNeylpzG6oNz5R4U4UjYXo6xIH1bYGSfNmqD9a+9H42YRlNV4v6bSkQUkLkp5KanPzh5zxXr82i/HwXoX3vdsS3Bm9qLetKEI1ndjpHy2W8P84Y09GEzAGzP6nBWwBc7kMbCgO6BoAAAAASUVORK5CYII=');left:0.4em;top:0.25em;position:relative}";

var MenuState;
(function (MenuState) {
  MenuState["MAIN"] = "main";
  MenuState["LANGUAGE"] = "language";
  MenuState["QUALITY"] = "quality";
  MenuState["NONE"] = "none";
})(MenuState || (MenuState = {}));
let AviaSettings$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.actionEvent = createEvent(this, "action", 7);
    this.settings = null;
    this.localeData = null;
    this.qualityLabel = 0;
    this.menuState = MenuState.NONE;
    this.menuButtonClass = 'avia-menu-button';
    this.menuButtonSelector = '.avia-menu-button:not([disabled])';
    this.qualityIndex = -1;
  }
  onClick(event) {
    const el = event.target;
    const action = el.dataset.action;
    const key = el.dataset.key;
    const value = el.dataset.value;
    if (action) {
      this.menuState = action;
    }
    if (key) {
      this.selectListItem(el);
    }
    if (key && value) {
      if (key == 'quality') {
        this.qualityIndex = parseInt(value);
      }
      this.actionEvent.emit({
        action: key == 'quality' ? Action.QUALITY_SELECT : Action.LANGUAGE_SELECT,
        value: key == 'quality' ? { index: value } : { trackType: key, trackId: value },
      });
    }
  }
  onKeyDown(event) {
    var _a, _b, _c;
    if (!((_a = this.settings) === null || _a === void 0 ? void 0 : _a.active) || this.menuState == MenuState.NONE) {
      return;
    }
    const active = document.activeElement;
    const menu = active.closest('.avia-list');
    if (!menu) {
      return;
    }
    const items = Array.from(menu.querySelectorAll(this.menuButtonSelector));
    if (!items) {
      return;
    }
    const findSibling = (items, start, predicate = (item) => item) => {
      const index = items.findIndex(item => item === start) + 1;
      const list = [...items.slice(index), ...items.slice(0, index)];
      return list.find(item => predicate(item));
    };
    const key = event.key;
    switch (key) {
      case 'Escape':
        this.close();
        break;
      case 'ArrowUp':
        const prev = findSibling(items.reverse(), active);
        prev === null || prev === void 0 ? void 0 : prev.focus();
        break;
      case 'ArrowDown':
        const next = findSibling(items, active);
        next === null || next === void 0 ? void 0 : next.focus();
        break;
      case 'ArrowLeft':
        if ((_b = menu.previousElementSibling) === null || _b === void 0 ? void 0 : _b.classList.contains('avia-list')) {
          const item = menu.previousElementSibling.querySelector(this.menuButtonSelector);
          item === null || item === void 0 ? void 0 : item.focus();
          return;
        }
        const back = document.querySelector('.avia-settings-back');
        if (back === null || back === void 0 ? void 0 : back.dataset.action) {
          this.menuState = back.dataset.action;
        }
        break;
      case 'ArrowRight':
        if ((_c = menu.nextElementSibling) === null || _c === void 0 ? void 0 : _c.classList.contains('avia-list')) {
          const item = menu.nextElementSibling.querySelector(this.menuButtonSelector);
          item === null || item === void 0 ? void 0 : item.focus();
          return;
        }
        if (active.dataset.action) {
          this.menuState = active.dataset.action;
        }
        break;
      default:
        if (/^[A-Za-z0-9]$/.test(key)) {
          const regex = new RegExp(`^${key}`, 'i');
          const item = findSibling(items, active, (item) => regex.test(item === null || item === void 0 ? void 0 : item.textContent));
          item === null || item === void 0 ? void 0 : item.focus();
        }
    }
  }
  async close() {
    this.actionEvent.emit({ action: Action.SETTINGS_OFF });
    this.menuState = MenuState.NONE;
  }
  componentWillRender() {
    var _a;
    if (((_a = this.settings) === null || _a === void 0 ? void 0 : _a.active) && this.menuState === MenuState.NONE) {
      this.menuState = MenuState.MAIN;
    }
  }
  disconnectedCallback() {
    clearTimeout(this.timeout);
  }
  render() {
    var _a;
    const st = this.menuState;
    if (!((_a = this.settings) === null || _a === void 0 ? void 0 : _a.active) || st === MenuState.NONE || !this.localeData) {
      return;
    }
    switch (this.menuState) {
      case MenuState.MAIN:
        return this.getMainMenu();
      case MenuState.LANGUAGE:
        return this.getLangMenu();
      case MenuState.QUALITY:
        return this.getQualityMenu();
    }
  }
  createDataAttributes(data) {
    return Object.entries(data).reduce((acc, entry) => {
      acc[`data-${entry[0]}`] = entry[1];
      return acc;
    }, {});
  }
  renderSubMenuItem({ label, data = {}, disabled = false }) {
    return (h("button", Object.assign({ class: this.menuButtonClass, type: "button", disabled: disabled, role: "menuitem", "aria-haspopup": "true" }, this.createDataAttributes(data)), label));
  }
  renderListItem({ label, data = {}, selected = false, disabled = false }) {
    return (h("button", Object.assign({ class: this.menuButtonClass, type: "button", disabled: disabled, role: "menuitemradio", "aria-checked": String(selected) }, this.createDataAttributes(data)), label));
  }
  renderList(name, title, items, factory) {
    return (h("div", { class: `avia-list avia-${name}-list` }, title && h("div", { class: "avia-list-title" }, title), h("div", { class: "avia-list-container" }, items.map(item => factory(item)))));
  }
  renderMenu(name, title, content) {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      const menu = document.querySelector(`.avia-${name}-menu`);
      const menuItem = menu === null || menu === void 0 ? void 0 : menu.querySelector(this.menuButtonSelector);
      menuItem === null || menuItem === void 0 ? void 0 : menuItem.focus();
    }, 0);
    return (h("div", { class: `avia-settings-menu avia-${name}-menu`, role: "menu" }, h("div", { class: "avia-settings-menu-header" }, name !== MenuState.MAIN && h("avia-button", { "loc-id": LocalizationId.BACK, class: "avia-settings-back", disabled: false, "data-action": MenuState.MAIN, icon: true }), h("span", null, title), h("span", { class: "avia-spacer" }), h("avia-button", { "loc-id": LocalizationId.CLOSE, class: "avia-menu-close", actions: Action.SETTINGS_OFF, disabled: false, icon: true })), h("div", { class: "avia-menu-content" }, content)));
  }
  getMainMenu() {
    var _a, _b, _c, _d;
    const items = [
      {
        label: this.localeData[LocalizationId.LANGUAGE],
        data: { action: MenuState.LANGUAGE },
        haspopup: true,
        disabled: !(((_a = this.settings.textTrackInfo) === null || _a === void 0 ? void 0 : _a.track) || ((_b = this.settings.audioTrackInfo) === null || _b === void 0 ? void 0 : _b.track)),
      }, {
        label: this.localeData[LocalizationId.QUALITY],
        data: { action: MenuState.QUALITY },
        haspopup: true,
        disabled: !(((_d = (_c = this.settings.qualityInfo) === null || _c === void 0 ? void 0 : _c.qualities) === null || _d === void 0 ? void 0 : _d.length) > 0),
      },
    ];
    return this.renderMenu(MenuState.MAIN, this.localeData[LocalizationId.SETTINGS], this.renderList('settings', null, items, this.renderSubMenuItem.bind(this)));
  }
  getLangMenu() {
    const tInfo = this.settings.textTrackInfo;
    const aInfo = this.settings.audioTrackInfo;
    const aTrk = aInfo === null || aInfo === void 0 ? void 0 : aInfo.track;
    const tTrk = tInfo === null || tInfo === void 0 ? void 0 : tInfo.track;
    const aTracks = aInfo === null || aInfo === void 0 ? void 0 : aInfo.tracks;
    const tTracks = tInfo === null || tInfo === void 0 ? void 0 : tInfo.tracks;
    const selText = tTrk === null || tTrk === void 0 ? void 0 : tTrk.id;
    const selAudio = aTrk === null || aTrk === void 0 ? void 0 : aTrk.index;
    const factory = this.renderListItem.bind(this);
    const menus = [];
    if (tTracks && tTracks.length) {
      menus.push(this.renderList('text', this.localeData[LocalizationId.CAPTIONS], tTracks.map((track) => ({
        selected: track.id === selText,
        disabled: tTracks.length === 1,
        label: this.getLangLabel(track),
        data: {
          key: 'text',
          value: track.id,
        },
      })), factory));
    }
    if (aTracks && aTracks.length) {
      menus.push(this.renderList('audio', this.localeData[LocalizationId.AUDIO], aTracks.map((track) => ({
        selected: track.index === selAudio,
        disabled: aTracks.length === 1,
        label: this.getLangLabel(track),
        data: {
          key: 'audio',
          value: track.index,
          description: track.type === 'description',
        },
      })), factory));
    }
    return this.renderMenu(MenuState.LANGUAGE, this.localeData[LocalizationId.LANGUAGE], menus);
  }
  getQualityMenu() {
    const qualities = this.settings.qualityInfo.qualities;
    const unique = qualities.reduce((acc, qual) => {
      const label = this.getQualityLabel(qual);
      const item = acc.find(i => i.label == label);
      if (!item) {
        acc.push({
          label,
          data: {
            key: 'quality',
            value: qual.index,
            bitrate: qual.bitrate,
          },
        });
      }
      else if (qual.bitrate > item.data.bitrate) {
        item.data.value = qual.index;
        item.data.bitrate = qual.bitrate;
      }
      return acc;
    }, []);
    const disabled = unique.length === 1;
    const items = unique
      .map((qual) => (Object.assign(Object.assign({}, qual), { selected: disabled || qual.data.index === this.qualityIndex, disabled })))
      .sort((a, b) => b.data.bitrate - a.data.bitrate);
    if (items.length > 1) {
      const autoLabel = this.getQualityLabel(this.settings.qualityInfo.quality);
      items.unshift({
        selected: this.qualityIndex === -1,
        disabled: false,
        label: `${this.localeData[LocalizationId.AUTO]} (${autoLabel})`,
        data: {
          key: 'quality',
          value: -1,
        },
      });
    }
    return this.renderMenu(MenuState.QUALITY, this.localeData[LocalizationId.QUALITY], this.renderList('quality', null, items, this.renderListItem.bind(this)));
  }
  getLangLabel(track) {
    const lang = track.language || track.lang || '';
    const tag = lang.split('-').shift().slice(0, 2).toUpperCase();
    const lbl = LanguageLabel[tag];
    return lbl || this.localeData[LocalizationId.UNKNOWN];
  }
  getQualityLabel(quality) {
    var _a;
    if ((_a = quality.category) === null || _a === void 0 ? void 0 : _a.length) {
      return quality.category[this.qualityLabel || 0];
    }
  }
  selectListItem(el) {
    const sel = el.parentElement.querySelector('[aria-checked=true]');
    if (sel) {
      // @ts-ignore
      sel.setAttribute('aria-checked', false);
    }
    // @ts-ignore
    el.setAttribute('aria-checked', true);
  }
  get el() { return this; }
  static get style() { return aviaSettingsCss; }
};
openWormhole(AviaSettings$1, ['settings', 'localeData', 'qualityLabel']);

const aviaSettingsToggleCss = "avia-settings-toggle{display:inline-block;position:relative}avia-settings-toggle avia-button{background-image:var(--avia-settings-icon)}avia-settings-toggle avia-button:hover{background-image:var(--avia-settings-icon-hover)}";

let AviaSettingsToggle$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.settingsDisabled = true;
    this.excludedControls = null;
  }
  render() {
    var _a, _b;
    if ((_a = this.excludedControls) === null || _a === void 0 ? void 0 : _a.includes(ControlFunction.SETTINGS)) {
      return;
    }
    const toggled = ((_b = this.settings) === null || _b === void 0 ? void 0 : _b.active) === true;
    return (h("avia-button", { actions: `${Action.SETTINGS_ON},${Action.SETTINGS_OFF}`, disabled: this.settingsDisabled, "loc-id": LocalizationId.SETTINGS, "aria-haspopup": 'true', "aria-expanded": toggled ? 'true' : 'false', toggled: toggled, icon: true }));
  }
  static get style() { return aviaSettingsToggleCss; }
};
openWormhole(AviaSettingsToggle$1, ['settings', 'settingsDisabled', 'excludedControls']);

const aviaSliderCss = ":root{--avia-slider-height:12px;--avia-slider-color:var(--avia-accent-color);--avia-slider-handle-color:var(--avia-accent-color);--avia-slider-track-color:#444;--avia-slider-track-radius:2px;--avia-slider-value-height:4px;--avia-slider-handle-width:12px;--avia-slider-handle-height:12px;--avia-slider-handle-radius:12px}avia-slider{display:block;height:var(--avia-slider-height);cursor:pointer;position:relative}.avia-slider-track{position:absolute;width:100%;height:var(--avia-slider-value-height);top:calc( (var(--avia-slider-height) - var(--avia-slider-value-height)) * 0.5);background:var(--avia-slider-track-color);border-radius:var(--avia-slider-track-radius)}.avia-slider-value{position:absolute;height:var(--avia-slider-value-height);top:calc( (var(--avia-slider-height) - var(--avia-slider-value-height)) * 0.5);background-color:var(--avia-slider-color);border-radius:var(--avia-slider-track-radius)}.avia-slider-handle{position:absolute;top:calc((var(--avia-slider-handle-height) - var(--avia-slider-height)) / -2);width:var(--avia-slider-handle-width);height:var(--avia-slider-handle-height);margin-left:calc(var(--avia-slider-handle-width) / -2);background-color:var(--avia-slider-handle-color);border-radius:var(--avia-slider-handle-radius);cursor:pointer;border:0;padding:0;outline:none}.avia-slider-list{position:absolute;width:100%;height:var(--avia-slider-value-height);top:calc( (var(--avia-slider-height) - var(--avia-slider-value-height)) * 0.5);background:transparent}.avia-slider-list-item{background-color:var(--avia-segment-marker-color);width:var(--avia-segment-marker-width);height:var(--avia-segment-marker-height);position:absolute;top:0}avia-slider:focus,avia-slider.focus-visible{outline:none}avia-slider.focus-visible .avia-slider-handle{box-shadow:var(--avia-focus-box-shadow)}avia-slider:focus,avia-slider .focus-visible{box-shadow:none}";

let AviaSlider$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.changeEvent = createEvent(this, "change", 7);
    this.inputEvent = createEvent(this, "input", 7);
    this.draggingEvent = createEvent(this, "dragging", 7);
    /**
     * The minimum slider value
     */
    this.min = 0;
    /**
     * The maximum slider value
     */
    this.max = 1;
    /**
     * The slider value
     */
    this.value = 1;
    /**
     * A list of data points to be rendered in the slider.
     */
    this.list = [];
    /**
     * The increment value used when changing the slider via the keyboard.
     */
    this.increment = 0.1;
    this.valueInternal = this.value;
    this.dragging = false;
    this.focused = false;
  }
  onValueChange(value) {
    if (this.dragging) {
      return;
    }
    if (value < this.min) {
      this.value = this.min;
      return;
    }
    if (value > this.max) {
      this.value = this.max;
      return;
    }
    this.valueInternal = value;
  }
  onMinChange(value) {
    this.value = mapToRange(this.value, 0, 1, value, this.max);
  }
  onMaxChange(value) {
    this.value = mapToRange(this.value, 0, 1, this.min, value);
  }
  onListChange(value) {
    if (value) {
      return;
    }
    this.list = [];
  }
  onMouseDown(event) {
    // @ts-ignore
    if (event.which > 1 || event.button > 0) {
      return;
    }
    blockEvent(event);
    this.dragging = true;
    this.draggingEvent.emit({ value: true });
    this.move(event);
  }
  onMouseMove(event) {
    if (!this.dragging) {
      return;
    }
    blockEvent(event);
    this.move(event);
  }
  onMouseUp(event) {
    if (this.dragging) {
      blockEvent(event);
      this.dragging = false;
      this.draggingEvent.emit({ value: false });
      this.move(event);
      this.change(this.valueInternal);
    }
  }
  onFocus() {
    if (!this.el.classList.contains("focus-visible")) {
      return;
    }
    this.focused = true;
  }
  onBlur() {
    if (!this.focused) {
      return;
    }
    this.focused = false;
  }
  onKeydown(event) {
    const code = event.keyCode;
    const action = (v) => {
      const value = this.value += v;
      this.input(value);
      this.change(value);
      blockEvent(event);
    };
    switch (code) {
      case 38:
      case 39:
      case 33: // page up
        action(this.increment);
        break;
      case 37:
      case 40:
      case 34: // page down
        action(-this.increment);
        break;
    }
  }
  onKeyup(event) {
    // block player level key commands
    const code = event.keyCode;
    if ([33, 34, 37, 38, 39, 40].indexOf(code) != -1) {
      blockEvent(event);
    }
  }
  move(event) {
    const offset = this.el.getBoundingClientRect();
    const pos = getEventPos(event);
    const percent = clampValue((pos.x - offset.left) / offset.width, 0, 1);
    const value = mapToRange(percent, 0, 1, this.min, this.max);
    this.input(value);
  }
  toPercent(value) {
    return toCssPercent(mapToRange(value, this.min, this.max));
  }
  input(v) {
    const previous = this.valueInternal;
    const value = clampValue(v, this.min, this.max);
    this.valueInternal = value;
    this.inputEvent.emit({ value, previous });
  }
  change(v) {
    const previous = this.value;
    const value = clampValue(v, this.min, this.max);
    this.valueInternal = value;
    this.value = value;
    this.changeEvent.emit({ value, previous });
  }
  render() {
    var _a;
    const percent = this.toPercent(this.valueInternal);
    const ariaLabel = (_a = this.localeData) === null || _a === void 0 ? void 0 : _a[this.locId];
    return (h(Host, { className: "avia-slider", role: "slider", "aria-label": ariaLabel, "aria-valuemin": this.min, "aria-valuemax": this.max, "aria-valuenow": this.value, "aria-valuetext": this.ariaValuetext || percent, tabIndex: 0 }, h("div", { class: "avia-slider-track" }), h("div", { class: "avia-slider-value", style: { width: percent } }), h("div", { class: "avia-slider-list" }, this.list.map((item) => h("div", { class: "avia-slider-list-item", style: { left: this.toPercent(item.value) } }))), h("slot", null), h("button", { class: "avia-slider-handle", type: "button", style: { left: percent }, tabIndex: -1 })));
  }
  get el() { return this; }
  static get watchers() { return {
    "value": ["onValueChange"],
    "min": ["onMinChange"],
    "max": ["onMaxChange"],
    "list": ["onListChange"]
  }; }
  static get style() { return aviaSliderCss; }
};
openWormhole(AviaSlider$1, ['localeData']);

const aviaSpinnerCss = "avia-spinner{display:block;width:var(--avia-spinner-diameter);height:var(--avia-spinner-diameter);overflow:visible;background-size:var(--avia-spinner-diameter);background-repeat:no-repeat;background-image:var(--avia-spinner-image);animation:avia-rotate 2s linear infinite}@keyframes avia-rotate{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}";

let AviaSpinner$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  render() {
    return (h(Host, null));
  }
  static get style() { return aviaSpinnerCss; }
};

const aviaTextCss = "avia-text{color:var(--avia-control-bar-btn-text-color)}";

let AviaText$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  render() {
    var _a;
    return (h(Host, null, (_a = this.localeData) === null || _a === void 0 ? void 0 : _a[this.locId]));
  }
  static get style() { return aviaTextCss; }
};
openWormhole(AviaText$1, ['localeData']);

const aviaTextToggleCss = "avia-text-toggle{display:inline-block}avia-text-toggle avia-button{background-image:var(--avia-text-on-icon)}avia-text-toggle avia-button:hover{background-image:var(--avia-text-on-icon-hover)}avia-text-toggle avia-button[toggled]{background-image:var(--avia-text-off-icon)}avia-text-toggle avia-button[toggled]:hover{background-image:var(--avia-text-off-icon-hover)}";

let AviaTextToggle$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.textTrackEnabled = false;
    this.textDisabled = true;
    this.excludedControls = null;
  }
  render() {
    var _a;
    if ((_a = this.excludedControls) === null || _a === void 0 ? void 0 : _a.includes(ControlFunction.TEXT)) {
      return;
    }
    return (h("avia-button", { ids: `${LocalizationId.CLOSED_CAPTIONS_ON}, ${LocalizationId.CLOSED_CAPTIONS_OFF}`, actions: `${Action.TEXT_ON}, ${Action.TEXT_OFF}`, disabled: this.textDisabled, toggled: this.textTrackEnabled, icon: true }));
  }
  static get style() { return aviaTextToggleCss; }
};
openWormhole(AviaTextToggle$1, [
  'textTrackEnabled',
  'textDisabled',
  'excludedControls',
]);

const aviaTimeDisplayCss = "avia-time-display{display:inline-block;margin-right:var(--avia-default-spacing)}.avia-time-display{display:inline-block;user-select:none;height:var(--avia-time-height);line-height:var(--avia-time-line-height);font-size:var(--avia-time-font-size);color:var(--avia-time-color);font-family:var(--avia-time-font-family)}.avia-control-group-right .avia-time-display{text-align:right}";

let AviaTimeDisplay$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.isLiveLinear = false;
    this.isDvr = false;
  }
  render() {
    const inactive = this.isLiveLinear || this.isDvr;
    if (inactive) {
      return '';
    }
    return (h("div", { class: 'avia-time-display' }, this.timeDisplayValue));
  }
  static get style() { return aviaTimeDisplayCss; }
};
openWormhole(AviaTimeDisplay$1, [
  'timeDisplayValue',
  'currentTimeSeconds',
  'durationSeconds',
  'isLiveLinear',
  'isDvr'
]);

const aviaUiCss = ":root{--avia-accent-color:#3aacff;--avia-text-color:#dedede;--avia-text-color-hover:#ffffff;--avia-font-family:Arial, sans-serif;--avia-control-bar-bg:linear-gradient(to top, rgba(2,0,36,0.8) 0%, rgba(1,0,10,0.5029586834733894) 62%, rgba(0,0,0,0) 100%);--avia-control-bar-height:64px;--avia-default-spacing:12px;--avia-half-spacing:6px;--avia-live-padding-top:12px;--avia-compact-button-width:48px;--avia-compact-button-height:48px;--avia-compact-button-border-radius:calc(var(--avia-compact-button-width) / 2);--avia-compact-button-background-color:rgba(64, 64, 64, 0.5);--avia-compact-control-bar-width:calc( (var(--avia-compact-button-width) * 3) + (var(--avia-default-spacing) * 2));--avia-compact-control-bar-margin-left:calc(var(--avia-compact-control-bar-width) / -2);--avia-compact-control-bar-margin-top:calc(var(--avia-compact-button-height) / -2);--avia-play-overlay-bg-color:rgba(0, 0, 0, 0.35);--avia-play-cta-width:66px;--avia-play-cta-height:66px;--avia-play-cta-border-radius:calc(var(--avia-play-cta-width) / 2);--avia-play-cta-bg-image:var(--avia-play-icon);--avia-play-cta-bg-position:50%;--avia-play-cta-bg-color:rgba(64, 64, 64, 0.5);--avia-play-cta-bg-size:calc(var(--avia-play-cta-width) / 2) calc(var(--avia-play-cta-height) / 2);--avia-control-bar-btn-height:48px;--avia-control-bar-btn-width:48px;--avia-control-bar-btn-text-color:var(--avia-text-color);--avia-control-bar-btn-text-color-hover:var(--avia-text-color-hover);--avia-control-bar-btn-border:none;--avia-control-bar-btn-border-width:0;--avia-control-bar-btn-border-radius:0;--avia-control-bar-btn-border-outline:none;--avia-control-bar-btn-border-cursor:pointer;--avia-control-bar-btn-disabled-cursor:not-allowed;--avia-control-bar-btn-disabled-opacity:0.5;--avia-icon-btn-bg-width:50%;--avia-icon-btn-bg-height:37.5%;--avia-icon-btn-bg-size:var(--avia-icon-btn-bg-width) var(--avia-icon-btn-bg-height);--avia-fs-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22white%22%20viewBox%3D%220%200%2024%2018%22%3E%3Cpath%20fill%3D%22none%22%20d%3D%22M0%200h24v18H0z%22%2F%3E%3Cpath%20d%3D%22M1%207a1%201%200%200%201-1-1V1a1%201%200%200%201%201-1h5a1%201%200%200%201%200%202H2.5a.5.5%200%200%200-.5.5V6a1%201%200%200%201-1%201zM6%2018H1a1%201%200%200%201-1-1v-5a1%201%200%200%201%202%200v3.5a.5.5%200%200%200%20.5.5H6a1%201%200%200%201%200%202zM23%2018h-5a1%201%200%200%201%200-2h3.5a.5.5%200%200%200%20.5-.5V12a1%201%200%200%201%202%200v5a1%201%200%200%201-1%201zM23%207a1%201%200%200%201-1-1V2.5a.5.5%200%200%200-.5-.5H18a1%201%200%200%201%200-2h5a1%201%200%200%201%201%201v5a1%201%200%200%201-1%201z%22%2F%3E%3C%2Fsvg%3E');--avia-fs-icon-hover:var(--avia-fs-icon);--avia-fs-exit-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22white%22%20viewBox%3D%220%200%2024%2018%22%3E%3Cpath%20fill%3D%22none%22%20d%3D%22M0%200h24v18H0z%22%2F%3E%3Cpath%20d%3D%22M4%206H1a1%201%200%200%201%200-2h2.5a.5.5%200%200%200%20.5-.5V1a1%201%200%200%201%202%200v3a2%202%200%200%201-2%202zM23%206h-4a1%201%200%200%201-1-1V1a1%201%200%200%201%202%200v2.5a.5.5%200%200%200%20.5.5H23a1%201%200%200%201%200%202zM19%2018a1%201%200%200%201-1-1v-3a2%202%200%200%201%202-2h3a1%201%200%200%201%200%202h-2.5a.5.5%200%200%200-.5.5V17a1%201%200%200%201-1%201zM5%2018a1%201%200%200%201-1-1v-2.5a.5.5%200%200%200-.5-.5H1a1%201%200%200%201%200-2h3a2%202%200%200%201%202%202v3a1%201%200%200%201-1%201z%22%2F%3E%3C%2Fsvg%3E');--avia-fs-exit-icon-hover:var(--avia-fs-exit-icon);--avia-play-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22white%22%20viewBox%3D%220%200%2024%2018%22%3E%3Ctitle%3EPlay%3C%2Ftitle%3E%3Cpath%20fill%3D%22none%22%20d%3D%22M0%200h24v18H0z%22%2F%3E%3Cpath%20d%3D%22M19%2010L6.84%2017.81a1.19%201.19%200%200%201-1.84-1V1.19a1.19%201.19%200%200%201%201.83-1L19%208a1.19%201.19%200%200%201%200%202z%22%2F%3E%3C%2Fsvg%3E');--avia-play-icon-hover:var(--avia-play-icon);--avia-pause-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22white%22%20viewBox%3D%220%200%2024%2018%22%3E%3Ctitle%3EPause%3C%2Ftitle%3E%3Cpath%20fill%3D%22none%22%20d%3D%22M0%200h24v18H0z%22%2F%3E%3Crect%20x%3D%226%22%20width%3D%224%22%20height%3D%2218%22%20rx%3D%221%22%2F%3E%3Crect%20x%3D%2214%22%20width%3D%224%22%20height%3D%2218%22%20rx%3D%221%22%2F%3E%3C%2Fsvg%3E');--avia-pause-icon-hover:var(--avia-pause-icon);--avia-stop-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22white%22%20viewBox%3D%220%200%2024%2018%22%3E%3Ctitle%3EStop%3C%2Ftitle%3E%3Cpath%20fill%3D%22none%22%20d%3D%22M0%200h24v18H0z%22%2F%3E%3Crect%20x%3D%223%22%20width%3D%2218%22%20height%3D%2218%22%20rx%3D%221%22%2F%3E%3C%2Fsvg%3E');--avia-stop-icon-hover:var(--avia-stop-icon);--avia-mute-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22white%22%20viewBox%3D%220%200%2024%2018%22%3E%3Cpath%20fill%3D%22none%22%20d%3D%22M0%200h24v18H0z%22%2F%3E%3Cpath%20d%3D%22M3.58%206H2a1%201%200%200%200-1%201v4a1%201%200%200%200%201%201h1.58a1%201%200%200%201%20.72.3l5%205a1%201%200%200%200%201.7-.74V1.44A1%201%200%200%200%209.27.73l-5%205a1%201%200%200%201-.69.27zM19.77%209.33a.5.5%200%200%201%200-.71l.94-.94a1%201%200%200%200%200-1.41%201%201%200%200%200-1.42%200l-.94.94a.5.5%200%200%201-.7%200l-.94-.94a1%201%200%200%200-1.42%200%201%201%200%200%200%200%201.41l.94.94a.5.5%200%200%201%200%20.71l-.94.94a1%201%200%200%200%200%201.41%201%201%200%200%200%201.42%200l.94-.94a.5.5%200%200%201%20.7%200l.94.94a1%201%200%200%200%201.42%200%201%201%200%200%200%200-1.41z%22%2F%3E%3C%2Fsvg%3E');--avia-mute-icon-hover:var(--avia-mute-icon);--avia-unmuted-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22white%22%20viewBox%3D%220%200%2024%2018%22%3E%3Cpath%20fill%3D%22none%22%20d%3D%22M0%200h24v18H0z%22%2F%3E%3Cpath%20d%3D%22M3.59%206H2a1%201%200%200%200-1%201v4a1%201%200%200%200%201%201h1.59a1%201%200%200%201%20.7.29l5%205a1%201%200%200%200%201.71-.7V1.41a1%201%200%200%200-1.71-.7l-5%205a1%201%200%200%201-.7.29zM14%2018a1%201%200%200%201%200-2%207%207%200%200%200%200-14%201%201%200%200%201%200-2%209%209%200%200%201%200%2018z%22%2F%3E%3Cpath%20d%3D%22M14%2014a1%201%200%200%201%200-2%203%203%200%200%200%200-6%201%201%200%200%201%200-2%205%205%200%200%201%200%2010z%22%2F%3E%3C%2Fsvg%3E');--avia-unmuted-icon-hover:var(--avia-unmuted-icon);--avia-text-on-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2018%22%3E%3Cdefs%3E%3Cstyle%3E.b%7Bfill%3Anone%3Bstroke%3A%23fff%3Bstroke-linecap%3Around%3Bstroke-miterlimit%3A10%7D%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cpath%20d%3D%22M23%200H1a1%201%200%2000-1%201v12a1%201%200%20001%201h2.5a.5.5%200%2001.5.5V17a1%201%200%2000.62.92A.84.84%200%20005%2018a1%201%200%2000.71-.29l3.56-3.56a.47.47%200%2001.35-.15H23a1%201%200%20001-1V1a1%201%200%2000-1-1zm-1%2011a1%201%200%2001-1%201H9a1%201%200%2000-.71.29L6%2014.59V13a1%201%200%2000-1-1H3a1%201%200%2001-1-1V3a1%201%200%20011-1h18a1%201%200%20011%201z%22%20fill%3D%22%23fff%22%2F%3E%3Cpath%20class%3D%22b%22%20d%3D%22M4.5%206.5h5M16.5%206.5h3M12.5%206.5h1M4.5%209.5h3M18.5%209.5h1M10.5%209.5h5%22%2F%3E%3C%2Fsvg%3E');--avia-text-on-icon-hover:var(--avia-text-on-icon);--avia-text-off-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2018%22%3E%3Cdefs%3E%3Cstyle%3E.b%7Bfill%3Anone%3Bstroke%3A%23231f20%3Bstroke-linecap%3Around%3Bstroke-miterlimit%3A10%7D%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cpath%20d%3D%22M23%200H1a1%201%200%2000-1%201v12a1%201%200%20001%201h2.5a.5.5%200%2001.5.5V17a1%201%200%2000.62.92A.84.84%200%20005%2018a1%201%200%2000.71-.29l3.56-3.56a.47.47%200%2001.35-.15H23a1%201%200%20001-1V1a1%201%200%2000-1-1z%22%20fill%3D%22%23fff%22%2F%3E%3Cpath%20class%3D%22b%22%20d%3D%22M4.5%206.5h5M16.5%206.5h3M12.5%206.5h1M4.5%209.5h3M18.5%209.5h1M10.5%209.5h5%22%2F%3E%3C%2Fsvg%3E');--avia-text-off-icon-hover:var(--avia-text-off-icon);--avia-text-icon-disabled:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2018%22%3E%3Cdefs%3E%3Cstyle%3E.b%7Bfill%3Anone%3Bstroke%3A%23fff%3Bstroke-linecap%3Around%3Bstroke-miterlimit%3A10%7D%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cpath%20d%3D%22M23%200H1a1%201%200%2000-1%201v12a1%201%200%20001%201h2.5a.5.5%200%2001.5.5V17a1%201%200%2000.62.92A.84.84%200%20005%2018a1%201%200%2000.71-.29l3.56-3.56a.47.47%200%2001.35-.15H23a1%201%200%20001-1V1a1%201%200%2000-1-1zm-1%2011a1%201%200%2001-1%201H9a1%201%200%2000-.71.29L6%2014.59V13a1%201%200%2000-1-1H3a1%201%200%2001-1-1V3a1%201%200%20011-1h18a1%201%200%20011%201z%22%20fill%3D%22%23fff%22%2F%3E%3Cpath%20class%3D%22b%22%20d%3D%22M4.5%206.5h5M16.5%206.5h3M12.5%206.5h1M4.5%209.5h3M18.5%209.5h1M10.5%209.5h5%22%2F%3E%3C%2Fsvg%3E');--avia-seek-forward-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2015.52%2017.29%22%3E%3Cpath%20d%3D%22M14.69%2C1.16%2C14%2C1.8A8.76%2C8.76%2C0%2C0%2C0%2C1.81%2C3.37%2C8.59%2C8.59%2C0%2C0%2C0%2C3.39%2C15.49a8.77%2C8.77%2C0%2C0%2C0%2C11.5-.74A1%2C1%2C0%2C0%2C0%2C15%2C13.4a1%2C1%2C0%2C0%2C0-1.36-.11l-.11.11a6.81%2C6.81%2C0%2C0%2C1-9.58%2C0%2C6.69%2C6.69%2C0%2C0%2C1-.06-9.51%2C6.82%2C6.82%2C0%2C0%2C1%2C8.73-.77L11.8%2C4a.47.47%2C0%2C0%2C0%2C0%2C.67.5.5%2C0%2C0%2C0%2C.37.15H15a.49.49%2C0%2C0%2C0%2C.49-.48V1.49a.47.47%2C0%2C0%2C0-.83-.33Z%22%20style%3D%22fill%3A%23fff%22%2F%3E%3C%2Fsvg%3E');--avia-seek-forward-icon-hover:var(--avia-seek-forward-icon);--avia-seek-back-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2015.52%2017.29%22%3E%3Cdefs%3E%3Cstyle%3E.cls-1%7Bfill%3A%23fff%3B%7D%3C%2Fstyle%3E%3C%2Fdefs%3E%3Cg%20id%3D%22Layer_2%22%20data-name%3D%22Layer%202%22%3E%3Cpath%20class%3D%22cls-1%22%20d%3D%22M.46%2C1A.48.48%2C0%2C0%2C0%2C0%2C1.49V4.33a.49.49%2C0%2C0%2C0%2C.48.48h2.9a.51.51%2C0%2C0%2C0%2C.37-.15.48.48%2C0%2C0%2C0%2C0-.67L2.9%2C3.17a6.82%2C6.82%2C0%2C0%2C1%2C8.73.77%2C6.69%2C6.69%2C0%2C0%2C1-.05%2C9.51A6.82%2C6.82%2C0%2C0%2C1%2C2%2C13.4a.52.52%2C0%2C0%2C0-.1-.11%2C1%2C1%2C0%2C0%2C0-1.37.11%2C1%2C1%2C0%2C0%2C0%2C.11%2C1.35%2C8.76%2C8.76%2C0%2C0%2C0%2C11.49.74A8.59%2C8.59%2C0%2C0%2C0%2C13.7%2C3.37%2C8.76%2C8.76%2C0%2C0%2C0%2C1.48%2C1.8L.82%2C1.16A.45.45%2C0%2C0%2C0%2C.46%2C1Z%22%20transform%3D%22translate(0%200)%22%2F%3E%3C%2Fg%3E%3C%2Fsvg%3E');--avia-seek-back-icon-hover:var(--avia-seek-back-icon);--avia-settings-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22white%22%20viewBox%3D%220%200%2024%2018%22%3E%3Cpath%20fill%3D%22none%22%20d%3D%22M0%200h24v18H0z%22%2F%3E%3Cpath%20d%3D%22M20.43%2010.4l-.93-.53a1%201%200%200%201%200-1.74l.93-.53a1%201%200%200%200%20.36-1.37l-2-3.46a1%201%200%200%200-1.36-.37l-.93.54a1%201%200%200%201-1.5-.87V1a1%201%200%200%200-1-1h-4a1%201%200%200%200-1%201v1.07a1%201%200%200%201-1.5.87l-.93-.54a1%201%200%200%200-1.36.37l-2%203.46a1%201%200%200%200%20.36%201.37l.93.53a1%201%200%200%201%200%201.74l-.93.53a1%201%200%200%200-.36%201.37l2%203.46a1%201%200%200%200%201.36.37l.93-.54a1%201%200%200%201%201.5.87V17a1%201%200%200%200%201%201h4a1%201%200%200%200%201-1v-1.07a1%201%200%200%201%201.5-.87l.93.54a1%201%200%200%200%201.36-.37l2-3.46a1%201%200%200%200-.36-1.37zM12%2012a3%203%200%201%201%203-3%203%203%200%200%201-3%203z%22%2F%3E%3C%2Fsvg%3E');--avia-settings-icon-hover:var(--avia-settings-icon);--avia-selected-icon:url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22white%22%3E%3Cpath%20d%3D%22M20.285%202l-11.285%2011.567-5.286-5.011-3.714%203.716%209%208.728%2015-15.285z%22%2F%3E%3C%2Fsvg%3E');--avia-time-height:48px;--avia-time-width:86px;--avia-time-line-height:48px;--avia-time-font-size:13px;--avia-time-color:var(--avia-text-color);--avia-time-font-family:var(--avia-font-family);--avia-segment-marker-color:var(--avia-text-color);--avia-segment-marker-height:4px;--avia-segment-marker-width:2px;--avia-ad-progress-color:#ffe83a;--avia-ad-progress-track-color:transparent;--avia-ad-control-bg-color:rgba(0, 0, 0, 0.7);--avia-ad-control-border-radius:4px;--avia-seek-indicator-border-radius:4px;--avia-seek-indicator-background-color:rgba(0, 0, 0, 0.5);--avia-seek-indicator-color:var(--avia-text-color);--avia-seek-indicator-height:48px;--avia-seek-indicator-font-size:20px;--avia-seek-indicator-font-family:var(--avia-font-family);--avia-seek-indicator-line-height:48px;--avia-volume-indicator-border-radius:6px;--avia-volume-indicator-background-color:rgba(0, 0, 0, 0.5);--avia-volume-indicator-width:64px;--avia-volume-indicator-height:64px;--avia-volume-indicator-right:12px;--avia-volume-indicator-bottom:72px;--avia-focus-box-shadow:0 0 2px 3px rgba(247, 228, 85, 0.85);--avia-focus-box-shadow-inner:inset 0 0 2px 3px rgba(247, 228, 85, 0.85);--avia-spinner-image:url('data:image/svg+xml,<svg xmlns=\"http://www.w3.org/2000/svg\" stroke=\"white\" stroke-width=\"1\" width=\"40\" height=\"40\" fill=\"white\" viewBox=\"0 0 37.84 41\"><path d=\"M18,39.61V32.09a.93.93,0,0,1,.26-.66.85.85,0,0,1,.64-.27.83.83,0,0,1,.63.27.89.89,0,0,1,.26.66v7.52a.91.91,0,0,1-.89.89.85.85,0,0,1-.64-.27.87.87,0,0,1-.26-.62Zm-6.4-10.27a.89.89,0,0,1,.59-.37.82.82,0,0,1,.65.16.79.79,0,0,1,.36.59.94.94,0,0,1-.15.69L8.8,36.47a.8.8,0,0,1-.57.38,1,1,0,0,1-.67-.16.84.84,0,0,1-.36-.59,1,1,0,0,1,.16-.7ZM1.13,25.53,8,23.21a.92.92,0,0,1,.67.05.93.93,0,0,1,.4,1.23,1,1,0,0,1-.52.47L1.64,27.27A.87.87,0,0,1,1,27.22a.82.82,0,0,1-.43-.53,1,1,0,0,1,0-.72.81.81,0,0,1,.54-.44Zm.51-11.8L8.56,16a1,1,0,0,1,.52.47.93.93,0,0,1-.4,1.23.92.92,0,0,1-.67.05L1.13,15.47A.81.81,0,0,1,.59,15a1,1,0,0,1,0-.72A.82.82,0,0,1,1,13.78a.87.87,0,0,1,.67,0ZM8.8,4.53l4.27,6.06a.94.94,0,0,1,.15.69.79.79,0,0,1-.36.59.82.82,0,0,1-.65.16.89.89,0,0,1-.59-.37L7.36,5.6a1,1,0,0,1-.16-.7.84.84,0,0,1,.36-.59,1,1,0,0,1,.67-.16.8.8,0,0,1,.57.38Zm11-3.14V8.91a.89.89,0,0,1-.26.66.83.83,0,0,1-.63.27.85.85,0,0,1-.64-.27A.93.93,0,0,1,18,8.91V1.39a.87.87,0,0,1,.26-.62A.85.85,0,0,1,18.92.5a.83.83,0,0,1,.63.27.83.83,0,0,1,.26.62ZM30.48,5.6l-4.27,6.06a.87.87,0,0,1-.58.37.85.85,0,0,1-.66-.16.86.86,0,0,1-.36-.59,1,1,0,0,1,.16-.69L29,4.53a.76.76,0,0,1,.56-.38.94.94,0,0,1,.67.16.86.86,0,0,1,.37.59,1,1,0,0,1-.16.7Zm6.23,9.87-6.88,2.32a.91.91,0,0,1-.67-.05.93.93,0,0,1-.4-1.23,1,1,0,0,1,.52-.47l6.91-2.31a.87.87,0,0,1,.67,0,.83.83,0,0,1,.44.53,1,1,0,0,1-.06.72.79.79,0,0,1-.53.44Zm-.52,11.8L29.28,25a1,1,0,0,1-.52-.47.93.93,0,0,1,.4-1.23.91.91,0,0,1,.67-.05l6.88,2.32a.79.79,0,0,1,.53.44,1,1,0,0,1,.06.72.83.83,0,0,1-.44.53.87.87,0,0,1-.67.05ZM29,36.47l-4.27-6.06a1,1,0,0,1-.16-.69.86.86,0,0,1,.36-.59.85.85,0,0,1,.66-.16.87.87,0,0,1,.58.37l4.27,6.06a1,1,0,0,1,.16.7.86.86,0,0,1-.37.59.94.94,0,0,1-.67.16.76.76,0,0,1-.56-.38Z\"/></svg>');--avia-spinner-diameter:48px;--avia-active-transition-in:opacity 0.5s ease 0s;--avia-active-transition-out:opacity 0.5s ease 0s}avia-ui{color:var(--avia-text-color);font-family:var(--avia-font-family)}avia-ui>*{pointer-events:auto;-webkit-user-select:none;user-select:none}avia-ui,avia-ui avia-accessibility,avia-ui avia-settings,.avia-layer{pointer-events:none;position:absolute;top:0;left:0;bottom:0;right:0}slot-fb[hidden]{display:none}.avia-ui-hidden{display:none}img.avia-poster-img{width:100%;height:auto}.avia-container,avia-ui:focus,avia-ui button:focus{outline:none}avia-ui.focus-visible,avia-ui .focus-visible{box-shadow:var(--avia-focus-box-shadow)}avia-ui.focus-visible .avia-menu-button,avia-ui .focus-visible.avia-menu-button{box-shadow:var(--avia-focus-box-shadow-inner)}avia-ui:not([active])[fullscreen]{cursor:none}.avia-container.avia-controls-vis video::-webkit-media-text-track-container{bottom:calc(var(--avia-control-bar-height) + 4px);height:auto}";

let AviaUi$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.actionEvent = createEvent(this, "uiaction", 7);
    this.controlMode = ControlMode.NONE;
    this.fullscreen = false;
    this.active = null;
    this.activeMode = ActiveMode.AUTO;
    this.excludedControls = null;
    this.fullscreenDisabled = true;
    this.muted = false;
    this.muteDisabled = true;
    this.volume = 1;
    this.playing = false;
    this.playDisabled = true;
    this.textTrackEnabled = false;
    this.textDisabled = true;
    this.timeDisplayValue = '';
    this.isLiveLinear = false;
    this.isDvr = false;
    this.live = false;
    this.playheadPosition = 0;
    this.durationSeconds = 0;
    this.currentTimeSeconds = 0;
    this.seekDisabled = true;
    this.seekBackDisabled = true;
    this.seekForwardDisabled = true;
    this.settingsDisabled = true;
    this.adPlayheadPosition = 0;
    this.localeData = null;
    this.preview = null;
    this.unmuteCtaVisible = false;
    this.settings = null;
    this.posterActive = false;
    this.posterImageUrl = '';
    this.qualityLabel = 0;
    this.waiting = false;
    this.initialized = false;
    this.pausable = true;
    this.isTouch = false;
  }
  handleAction(e) {
    this.actionEvent.emit(e.detail);
  }
  componentWillLoad() {
    // @ts-ignore
    Universe.create(this, this);
  }
  render() {
    if (!this.initialized) {
      return;
    }
    return (h(Host, null, h(Universe.Provider, { state: this }, h("div", { class: "avia-layer" }, h("slot", { name: "underlay" })), h("slot", { name: "play-overlay" }, h("avia-play-overlay", null)), h("slot", { name: "waiting-overlay" }, h("avia-waiting-overlay", null)), h("slot", { name: "unmute-cta" }, h("avia-unmute-cta", null)), h("slot", { name: "accessibility" }, h("avia-accessibility", null)), h("slot", { name: "settings" }, h("avia-settings", null)), h("slot", { name: "poster" }, h("avia-poster", null)), h("slot", { name: "control-bar" }, h("avia-control-bar", null)), h("slot", { name: "ad-control-bar" }, h("avia-ad-control-bar", null)), h("slot", { name: "compact-control-bar" }, h("avia-control-bar-compact", null)), h("div", { class: "avia-layer" }, h("slot", { name: "overlay" })))));
  }
  static get style() { return aviaUiCss; }
};

const aviaUnmuteCtaCss = "avia-unmute-cta{box-sizing:border-box;display:inline-block;position:absolute;top:64px;height:var(--avia-control-bar-btn-height);left:var(--avia-default-spacing);pointer-events:auto;background-color:rgba(0, 0, 0, 0.415);border-radius:4px;opacity:1}avia-unmute-cta avia-button{background-image:var(--avia-mute-icon);background-position:-31px center;background-repeat:no-repeat;background-size:60% 45%;line-height:var(--avia-control-bar-btn-height);padding-right:var(--avia-default-spacing);padding-left:50px;font-size:1.1em;text-align:left;width:auto;height:var(--avia-control-bar-btn-height);color:white;cursor:pointer;opacity:0.85}avia-unmute-cta avia-button:hover{background-image:var(--avia-mute-icon-hover)}";

let AviaUnmuteCta$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.muteState = false;
    this.unmuteCtaVisible = false;
  }
  render() {
    if (!this.unmuteCtaVisible || !this.muteState) {
      return '';
    }
    return (h("avia-button", { actions: Action.UNMUTE }, h("avia-text", { "loc-id": LocalizationId.CLICK_TO_UNMUTE })));
  }
  static get style() { return aviaUnmuteCtaCss; }
};
openWormhole(AviaUnmuteCta$1, ['muteState', 'unmuteCtaVisible']);

const aviaVolumeIndicatorCss = "avia-volume-indicator{position:absolute;display:block;box-sizing:border-box;border-radius:var(--avia-volume-indicator-border-radius);background-color:var(--avia-volume-indicator-background-color);width:0;height:0;right:var(--avia-volume-indicator-right);bottom:var(--avia-volume-indicator-bottom);visibility:hidden;padding-top:48px;opacity:0;transition-property:opacity;transition-duration:300ms;transition-timing-function:ease-in;box-shadow:0px 0px 1px 1px rgba(0, 0, 0, 0.3)}avia-volume-indicator:empty{opacity:0}.avia-acc-vol{width:48px;margin:0 auto;background-color:black;height:4px;position:relative}.avia-acc-vol-meter{height:4px;background-color:#fff}.avia-vol-meter-overlay{position:absolute;top:0;left:0;height:4px;background-image:url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAECAYAAACp8Z5+AAAAFElEQVQImWNAA/+Z0EUICDAwMAAANNwBBuf8FlcAAAAASUVORK5CYII=');width:100%}avia-volume-indicator.avia-indicator-active{width:var(--avia-volume-indicator-width);height:var(--avia-volume-indicator-height);opacity:1.0}avia-volume-indicator.avia-volume-on::before,avia-volume-indicator.avia-volume-off::before{content:\"\";position:absolute;top:0px;right:0px;bottom:0px;left:0px;opacity:0.85;background-repeat:no-repeat;background-position:center 12px;background-size:40% 30%}avia-volume-indicator.avia-volume-on::before{background-image:var(--avia-unmuted-icon)}avia-volume-indicator.avia-volume-on::before:hover{background-image:var(--avia-unmuted-icon-hover)}avia-volume-indicator.avia-volume-off::before{background-image:var(--avia-mute-icon)}avia-volume-indicator.avia-volume-off::before:hover{background-image:var(--avia-mute-icon-hover)}";

let AviaVolumeIndicator$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
  }
  componentDidRender() {
    var _a;
    if (!this.debouncer && this.accIndicatorState.displayTime > 0) {
      this.debouncer = debounce(() => {
        var _a;
        (_a = this.el) === null || _a === void 0 ? void 0 : _a.classList.remove(CssNames.INDICATOR_ACTIVE);
        this.visible = false;
      }, this.accIndicatorState.displayTime);
    }
    if (this.visible) {
      setTimeout(() => this.el.classList.add(CssNames.INDICATOR_ACTIVE), 0);
      (_a = this.debouncer) === null || _a === void 0 ? void 0 : _a.call(this);
    }
  }
  render() {
    var _a;
    const acc = this.accIndicatorState;
    if (!acc.volumeKey) {
      (_a = this.el) === null || _a === void 0 ? void 0 : _a.classList.remove(CssNames.INDICATOR_ACTIVE);
      this.visible = false;
      return '';
    }
    this.visible = true;
    const vol = this.volume === 0 ? 0 : clampValue(this.volume, 0, 1);
    return (h(Host, { class: acc.muted || this.volume == 0 ? CssNames.ACC_VOL_OFF : CssNames.ACC_VOL_ON }, h("div", { class: CssNames.ACC_VOL }, h("div", { class: CssNames.ACC_VOL_METER, style: { width: toCssPercent(vol) } }), h("div", { class: CssNames.ACC_VOL_OVERLAY }))));
  }
  get el() { return this; }
  static get style() { return aviaVolumeIndicatorCss; }
};
openWormhole(AviaVolumeIndicator$1, ['volume', 'accIndicatorState']);

const aviaVolumeSliderCss = "avia-volume-slider{display:flex;width:100px;margin-left:var(--avia-default-spacing)}avia-volume-slider avia-slider{width:100%;margin-top:-4px}";

let AviaVolumeSlider$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.changeEvent = createEvent(this, "action", 7);
    this.excludedControls = null;
  }
  onInput(e) {
    this.changeEvent.emit({ action: Action.VOLUME, value: e.detail.value });
  }
  onDragging(e) {
    if (e.detail.value !== true) {
      return;
    }
    this.changeEvent.emit({ action: Action.UNMUTE, value: true });
  }
  render() {
    var _a;
    if ((_a = this.excludedControls) === null || _a === void 0 ? void 0 : _a.includes(ControlFunction.VOLUME)) {
      return;
    }
    return (h("avia-slider", { "loc-id": LocalizationId.VOLUME, value: this.volume, onInput: (e) => this.onInput(e), onDragging: (e) => this.onDragging(e) }));
  }
  static get style() { return aviaVolumeSliderCss; }
};
openWormhole(AviaVolumeSlider$1, [
  'volume',
  'excludedControls',
]);

const aviaWaitingOverlayCss = "avia-waiting-overlay{display:block;justify-content:center;align-items:center;display:flex}avia-waiting-overlay slot-fb:not(:empty)~avia-spinner{display:none}";

let AviaWaitingOverlay$1 = class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.waiting = false;
  }
  render() {
    if (!this.waiting) {
      return;
    }
    return (h("avia-overlay", { centered: true }, h("avia-spinner", null)));
  }
  static get style() { return aviaWaitingOverlayCss; }
};
openWormhole(AviaWaitingOverlay$1, ['waiting']);

const AviaAccessibility = /*@__PURE__*/proxyCustomElement(AviaAccessibility$1, [0,"avia-accessibility"]);
const AviaActive = /*@__PURE__*/proxyCustomElement(AviaActive$1, [4,"avia-active"]);
const AviaAdControlBar = /*@__PURE__*/proxyCustomElement(AviaAdControlBar$1, [0,"avia-ad-control-bar"]);
const AviaAdProgressBar = /*@__PURE__*/proxyCustomElement(AviaAdProgressBar$1, [0,"avia-ad-progress-bar",{"adPlayheadPosition":[2,"ad-playhead-position"],"isLiveLinear":[4,"is-live-linear"]}]);
const AviaButton = /*@__PURE__*/proxyCustomElement(AviaButton$1, [4,"avia-button",{"locId":[513,"loc-id"],"toggled":[516],"compact":[516],"icon":[516],"variant":[516],"actions":[1],"ids":[513],"disabled":[4],"localeData":[16],"focused":[32]},[[0,"click","onClick"],[1,"touchstart","onTouchStart"],[1,"touchend","onTouchEnd"],[0,"focus","onFocus"],[0,"blur","onBlur"],[0,"keyup","onKeyDown"]]]);
const AviaControlBar = /*@__PURE__*/proxyCustomElement(AviaControlBar$1, [4,"avia-control-bar",{"isLiveLinear":[4,"is-live-linear"],"controlMode":[1,"control-mode"]}]);
const AviaControlBarCompact = /*@__PURE__*/proxyCustomElement(AviaControlBarCompact$1, [4,"avia-control-bar-compact"]);
const AviaCurrentTime = /*@__PURE__*/proxyCustomElement(AviaCurrentTime$1, [0,"avia-current-time",{"currentTimeSeconds":[2,"current-time-seconds"],"localeData":[16],"isLiveLinear":[4,"is-live-linear"],"isDvr":[4,"is-dvr"]}]);
const AviaDuration = /*@__PURE__*/proxyCustomElement(AviaDuration$1, [0,"avia-duration",{"durationSeconds":[2,"duration-seconds"],"localeData":[16],"isLiveLinear":[4,"is-live-linear"],"isDvr":[4,"is-dvr"]}]);
const AviaFullscreenToggle = /*@__PURE__*/proxyCustomElement(AviaFullscreenToggle$1, [0,"avia-fullscreen-toggle",{"fullscreen":[4],"fullscreenDisabled":[4,"fullscreen-disabled"],"compact":[516]}]);
const AviaLiveControl = /*@__PURE__*/proxyCustomElement(AviaLiveControl$1, [0,"avia-live-control",{"isLiveLinear":[4,"is-live-linear"],"isDvr":[4,"is-dvr"],"live":[4]}]);
const AviaMuteToggle = /*@__PURE__*/proxyCustomElement(AviaMuteToggle$1, [0,"avia-mute-toggle",{"muted":[4],"muteDisabled":[4,"mute-disabled"],"compact":[516]}]);
const AviaOverlay = /*@__PURE__*/proxyCustomElement(AviaOverlay$1, [4,"avia-overlay",{"centered":[516]}]);
const AviaPlayCta = /*@__PURE__*/proxyCustomElement(AviaPlayCta$1, [0,"avia-play-cta"]);
const AviaPlayOverlay = /*@__PURE__*/proxyCustomElement(AviaPlayOverlay$1, [0,"avia-play-overlay",{"playing":[4],"controlMode":[1,"control-mode"],"playDisabled":[4,"play-disabled"],"pausable":[4],"isTouch":[4,"is-touch"]},[[2,"click","onClick"]]]);
const AviaPlayToggle = /*@__PURE__*/proxyCustomElement(AviaPlayToggle$1, [0,"avia-play-toggle",{"playing":[4],"playDisabled":[4,"play-disabled"],"pausable":[4],"compact":[516]}]);
const AviaPoster = /*@__PURE__*/proxyCustomElement(AviaPoster$1, [4,"avia-poster",{"posterActive":[4,"poster-active"],"posterImageUrl":[1,"poster-image-url"]},[[2,"click","handleAction"]]]);
const AviaProgressBar = /*@__PURE__*/proxyCustomElement(AviaProgressBar$1, [0,"avia-progress-bar",{"currentTimeSeconds":[2,"current-time-seconds"],"durationSeconds":[2,"duration-seconds"],"localeData":[16],"segmentBreakPositions":[1,"segment-break-positions"],"preview":[16],"increment":[2],"hovering":[32],"cursor":[32]},[[1,"mouseenter","onMouseEnter"],[1,"mouseleave","onMouseLeave"],[9,"mousemove","onMouseMove"]]]);
const AviaSeekBackButton = /*@__PURE__*/proxyCustomElement(AviaSeekBackButton$1, [0,"avia-seek-back-button",{"seekBackDisabled":[4,"seek-back-disabled"],"isLiveLinear":[4,"is-live-linear"]}]);
const AviaSeekForwardButton = /*@__PURE__*/proxyCustomElement(AviaSeekForwardButton$1, [0,"avia-seek-forward-button",{"seekForwardDisabled":[4,"seek-forward-disabled"],"isLiveLinear":[4,"is-live-linear"]}]);
const AviaSeekIndicator = /*@__PURE__*/proxyCustomElement(AviaSeekIndicator$1, [0,"avia-seek-indicator",{"durationSeconds":[2,"duration-seconds"],"accIndicatorState":[16]}]);
const AviaSettings = /*@__PURE__*/proxyCustomElement(AviaSettings$1, [0,"avia-settings",{"settings":[16],"localeData":[16],"qualityLabel":[2,"quality-label"],"menuState":[32]},[[0,"click","onClick"],[4,"keydown","onKeyDown"]]]);
const AviaSettingsToggle = /*@__PURE__*/proxyCustomElement(AviaSettingsToggle$1, [0,"avia-settings-toggle",{"settings":[16],"settingsDisabled":[4,"settings-disabled"],"excludedControls":[16]}]);
const AviaSlider = /*@__PURE__*/proxyCustomElement(AviaSlider$1, [4,"avia-slider",{"locId":[513,"loc-id"],"min":[2],"max":[2],"value":[1026],"list":[16],"ariaValuetext":[1,"aria-valuetext"],"increment":[2],"localeData":[16],"valueInternal":[32]},[[1,"touchstart","onMouseDown"],[0,"mousedown","onMouseDown"],[8,"touchmove","onMouseMove"],[8,"mousemove","onMouseMove"],[9,"touchend","onMouseUp"],[8,"mouseup","onMouseUp"],[0,"focus","onFocus"],[0,"blur","onBlur"],[0,"keydown","onKeydown"],[0,"keyup","onKeyup"]]]);
const AviaSpinner = /*@__PURE__*/proxyCustomElement(AviaSpinner$1, [0,"avia-spinner"]);
const AviaText = /*@__PURE__*/proxyCustomElement(AviaText$1, [0,"avia-text",{"locId":[513,"loc-id"],"localeData":[16]}]);
const AviaTextToggle = /*@__PURE__*/proxyCustomElement(AviaTextToggle$1, [0,"avia-text-toggle",{"textTrackEnabled":[4,"text-track-enabled"],"textDisabled":[4,"text-disabled"],"excludedControls":[16]}]);
const AviaTimeDisplay = /*@__PURE__*/proxyCustomElement(AviaTimeDisplay$1, [0,"avia-time-display",{"timeDisplayValue":[1,"time-display-value"],"currentTimeSeconds":[2,"current-time-seconds"],"durationSeconds":[2,"duration-seconds"],"isLiveLinear":[4,"is-live-linear"],"isDvr":[4,"is-dvr"]}]);
const AviaUi = /*@__PURE__*/proxyCustomElement(AviaUi$1, [4,"avia-ui",{"controlMode":[513,"control-mode"],"fullscreen":[516],"active":[516],"activeMode":[513,"active-mode"],"excludedControls":[16],"fullscreenDisabled":[4,"fullscreen-disabled"],"muted":[4],"muteDisabled":[4,"mute-disabled"],"volume":[2],"playing":[4],"playDisabled":[4,"play-disabled"],"textTrackEnabled":[4,"text-track-enabled"],"textDisabled":[4,"text-disabled"],"timeDisplayValue":[1,"time-display-value"],"isLiveLinear":[4,"is-live-linear"],"isDvr":[4,"is-dvr"],"live":[4],"playheadPosition":[2,"playhead-position"],"durationSeconds":[2,"duration-seconds"],"currentTimeSeconds":[2,"current-time-seconds"],"seekDisabled":[4,"seek-disabled"],"seekBackDisabled":[4,"seek-back-disabled"],"seekForwardDisabled":[4,"seek-forward-disabled"],"settingsDisabled":[4,"settings-disabled"],"adPlayheadPosition":[2,"ad-playhead-position"],"segmentBreakPositions":[1,"segment-break-positions"],"localeData":[16],"preview":[16],"accIndicatorState":[16],"unmuteCtaVisible":[4,"unmute-cta-visible"],"settings":[16],"posterActive":[4,"poster-active"],"posterImageUrl":[1,"poster-image-url"],"qualityLabel":[2,"quality-label"],"waiting":[4],"initialized":[4],"pausable":[4],"isTouch":[4,"is-touch"]},[[0,"action","handleAction"]]]);
const AviaUnmuteCta = /*@__PURE__*/proxyCustomElement(AviaUnmuteCta$1, [0,"avia-unmute-cta",{"muteState":[4,"mute-state"],"unmuteCtaVisible":[4,"unmute-cta-visible"]}]);
const AviaVolumeIndicator = /*@__PURE__*/proxyCustomElement(AviaVolumeIndicator$1, [0,"avia-volume-indicator",{"volume":[2],"accIndicatorState":[16]}]);
const AviaVolumeSlider = /*@__PURE__*/proxyCustomElement(AviaVolumeSlider$1, [0,"avia-volume-slider",{"volume":[2],"excludedControls":[16]}]);
const AviaWaitingOverlay = /*@__PURE__*/proxyCustomElement(AviaWaitingOverlay$1, [0,"avia-waiting-overlay",{"waiting":[4]}]);
const defineCustomElements = (opts) => {
  if (typeof customElements !== 'undefined') {
    [
      AviaAccessibility,
  AviaActive,
  AviaAdControlBar,
  AviaAdProgressBar,
  AviaButton,
  AviaControlBar,
  AviaControlBarCompact,
  AviaCurrentTime,
  AviaDuration,
  AviaFullscreenToggle,
  AviaLiveControl,
  AviaMuteToggle,
  AviaOverlay,
  AviaPlayCta,
  AviaPlayOverlay,
  AviaPlayToggle,
  AviaPoster,
  AviaProgressBar,
  AviaSeekBackButton,
  AviaSeekForwardButton,
  AviaSeekIndicator,
  AviaSettings,
  AviaSettingsToggle,
  AviaSlider,
  AviaSpinner,
  AviaText,
  AviaTextToggle,
  AviaTimeDisplay,
  AviaUi,
  AviaUnmuteCta,
  AviaVolumeIndicator,
  AviaVolumeSlider,
  AviaWaitingOverlay
    ].forEach(cmp => {
      if (!customElements.get(cmp.is)) {
        customElements.define(cmp.is, cmp, opts);
      }
    });
  }
};

export { ActiveMode, AviaAccessibility, AviaActive, AviaAdControlBar, AviaAdProgressBar, AviaButton, AviaControlBar, AviaControlBarCompact, AviaCurrentTime, AviaDuration, AviaFullscreenToggle, AviaLiveControl, AviaMuteToggle, AviaOverlay, AviaPlayCta, AviaPlayOverlay, AviaPlayToggle, AviaPoster, AviaProgressBar, AviaSeekBackButton, AviaSeekForwardButton, AviaSeekIndicator, AviaSettings, AviaSettingsToggle, AviaSlider, AviaSpinner, AviaText, AviaTextToggle, AviaTimeDisplay, AviaUi, AviaUiPlugin, AviaUnmuteCta, AviaVolumeIndicator, AviaVolumeSlider, AviaWaitingOverlay, LocalizationId, PreviewTimeTransform, create, createUi, defineCustomElements };
